import React, { useState, useEffect, useReducer, useCallback } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
// [FIX]: Removed direct import - now passed as prop to avoid lazy-loading crash
// import { getSpellsByClass } from '../data/srdSpells';
// [E-7 fix]: SRD_SPELLS imported for level lookup — replaces hardcoded SPELL_LIST
import { SRD_SPELLS } from '../data/srdSpells';
import { formatCurrency } from '../utils/currencyUtils';
import SearchableItemSelector from './SearchableItemSelector';
import { RECHARGE_TYPES } from '../data/rechargeTypes';
import { systemBus, EVENTS } from '../services/systemBus';
import { sourcesManager } from '../data/sourcesManager';
import { addItemToSource } from '../data/sources/initializeSources';
import { useTutorial } from '../contexts/TutorialContext';
import { getAllClasses, isSpellcaster } from '../utils/contentResolver';
import MobileModal from './MobileModal';
import { useConfirm } from '../contexts/ConfirmContext';
// [SUB-PLAN 1R — 2026-05-29]: EQUIPMENT_SLOT_LABELS removed — the equip-slot
//   field now uses <ReferencePicker kind="equipSlot"> (Stone 2). Registry reads
//   EQUIPMENT_SLOT_LABELS internally via composerCatalogRegistry.js. EditItemDialog
//   retains its own import; this dialog no longer needs it.
// [PHASE B / #030 — 2026-05-06]: Shared D&D 5e weapon-properties data
//   (id + label + PHB description). Picker iterates this and renders
//   each checkbox with a tooltip (title + aria-describedby) so authors
//   know what each property does without leaving the dialog. Closes
//   B1-4.
import { WEAPON_PROPERTIES } from '../data/weaponProperties';
// [SUB-PLAN 2F kind (b) — 2026-05-25]: ReferencePicker substitutes the
//   hand-rolled <select multiple> for conditionImmunities (Advanced
//   Properties). Pulls SRD ∪ playerStats.customConditions through the
//   composer registry; chip UI replaces the native Ctrl/Cmd multi-select
//   pattern. Behavior change vs pre-2F: drops `exhaustion` (multi-level,
//   tracked separately) and gains `invisible` (was missing from inline
//   list). The SRD list is canonical via src/data/srdConditions.js.
import ReferencePicker from './composer/ReferencePicker';
// [STONE 4 — 2026-05-26]: StatModificationsList replaces the single-slot
//   set/add/subtract picker. Canonical shape: [{ id, target, value }].
//   Adapter handles load from legacy shapes (single-slot and EditItemDialog object).
import StatModificationsList from './composer/StatModificationsList';
import {
  legacyToCanonical,
  objectToCanonical,
  canonicalToLegacy,
} from '../utils/itemStatModAdapter';
// [STONES 5-6 — 2026-05-26]: Wire canonical S1 effects array at save time so
//   buildItemRegistrationsFromInventory → effectRegistry picks up user-authored
//   item effects. Zero production callers before this commit (Rule 31 trace
//   at .claude/findings/2026-05-26-datamodel-item-effects-4e7d12.md).
import { buildEffectsFromForm } from '../utils/itemEffectsRegistration';
// [SUB-PLAN 1H-B — 2026-05-28]: Items become the 6th member of the CW
//   authoring quintet by importing the same FeatureEffectsSection that
//   EditItemDialog (Phase B / #026) uses. Mirror pattern: effects[] N-array
//   authored via the section merges with buildEffectsFromForm output at
//   handleSubmit time, identical to the EditItemDialog save path.
import {
  FeatureEffectsSection,
  emptyEffect,
} from './effectBuilder/EffectBuilderControls';
// [LANE-C DEFAULTS — 2026-06-13]: Parity with EditItemDialog charge-default wiring.
//   The InventoryItem constructor already calls resolveCurrentCharges at build time
//   (App.jsx:387), so the UI display is safe. Importing here and applying at save
//   time makes the intent explicit and guards against any future caller that reads
//   itemData.charges directly before the constructor runs.
import { resolveCurrentCharges } from '../utils/chargedItemDefaults';

/**
 * [STONE 4 — 2026-05-26]: Load any of the 3 historical item stat-mod shapes
 *   into the canonical array. Priority: canonical array > EditItemDialog object > legacy single-slot.
 *   Returns [] when no shape is present.
 */
function loadItemStatMods(item) {
  if (!item) return [];
  // Priority 1: canonical array (post-Stone-4 saves)
  if (Array.isArray(item.statModifications)) {
    return item.statModifications;
  }
  // Priority 2: EditItemDialog object shape
  if (item.statModifications && typeof item.statModifications === 'object') {
    return objectToCanonical(item.statModifications);
  }
  // Priority 3: legacy single-slot
  if (item.statBonus) {
    return legacyToCanonical(item.statBonus, item.statValue);
  }
  return [];
}

const ItemDialog = ({
  showItemDialog,
  setShowItemDialog,
  addStarterPack,
  addNewItem,
  BASE_ITEMS,
  STARTER_PACKS,
  getSpellsByClass, // [FIX]: Passed from App.jsx to avoid crash when data not loaded
  // [2026-05-25]: mode toggles which sections render. Default 'create'
  // (full edit fields, the historical behavior). When mode==='find',
  // the customize/edit fields hide — leaving the picker (search +
  // category browse + Currently Selected preview + qty + Add). Used
  // by Inventory's Add Item button so the same dialog serves both
  // Workshop authoring and Inventory finding without a parallel.
  mode = 'create',
}) => {
  const isFindMode = mode === 'find';
  // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled async confirm for the
  // discard-in-progress guard (falls back to window.confirm outside provider).
  const confirm = useConfirm();
  // [APR 2026]: Standardize Escape dismissal — clears all transient form state.
  // [PHASE B / #007 — B1-8]: Escape routes through `handleCloseDialog` so
  //   Escape, Cancel, and X all share the same confirm-before-discard guard.
  //   May 5 user report: typed customizations were lost when the dialog
  //   dismissed accidentally.
  // [UI-MOBILEMODAL-ADOPTION wave 2d — 2026-07-09]: the main shell now wraps
  //   MobileModal, which registers the Escape hook internally with
  //   onClose={handleCloseDialog} — same guarded path, no own call needed.

  const [category, setCategory] = useState('weapons');
  const [baseType, setBaseType] = useState('dagger');
  const [customName, setCustomName] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [customWeight, setCustomWeight] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [charges, setCharges] = useState('');
  const [maxCharges, setMaxCharges] = useState('');
  const [chargesPerDay, setChargesPerDay] = useState(false);
  const [rechargeType, setRechargeType] = useState(RECHARGE_TYPES.NONE);
  const [purchaseForFree, setPurchaseForFree] = useState(false);
  const [treasureType, setTreasureType] = useState('gems');
  const [treasureTier, setTreasureTier] = useState('10');
  const [treasureQuantity, setTreasureQuantity] = useState(1);
  const [treasureSize, setTreasureSize] = useState('tiny');
  const [quantity, setQuantity] = useState(1); // For trade goods and spell components
  const [equipSlot, setEquipSlot] = useState('');
  const [requiresAttunement, setRequiresAttunement] = useState(false);
  
  // Advanced properties state
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [material, setMaterial] = useState('');
  const [magicBonus, setMagicBonus] = useState('');
  const [damageDice, setDamageDice] = useState('');
  const [statBonus, setStatBonus] = useState(''); // e.g., "setStrength" — backward-compat mirror (Stone 4)
  const [statValue, setStatValue] = useState(''); // e.g., "19" — backward-compat mirror (Stone 4)
  // [STONE 4 — 2026-05-26]: Canonical stat modifications array
  const [statModifications, setStatModifications] = useState([]);

  // [SUB-PLAN 4L — 2026-05-28]: Ammunition Effects (on-hit) form fields.
  //   Visible only when category === 'ammunition'. Emits itemEffects[] in the
  //   SRD ammo shape so CW-authored ammo co-exists with SRD data at runtime.
  //   Storage shape: item.itemEffects[0].value.payload[] (triggeredEffect, on_hit).
  const [ammoEffectsEnabled, setAmmoEffectsEnabled] = useState(false);
  const [ammoAttackBonus, setAmmoAttackBonus] = useState('');
  const [ammoDamageBonusFlat, setAmmoDamageBonusFlat] = useState('');  // flat +N
  const [ammoDamageDice, setAmmoDamageDice] = useState('');            // e.g. "1d4"
  const [ammoDamageType, setAmmoDamageType] = useState('');            // e.g. "poison"
  const [ammoUseDice, setAmmoUseDice] = useState(false);               // flat vs dice
  const [ammoSaveEnabled, setAmmoSaveEnabled] = useState(false);
  const [ammoSaveDC, setAmmoSaveDC] = useState('');
  const [ammoSaveAbility, setAmmoSaveAbility] = useState('constitution');
  const [ammoHalfOnSave, setAmmoHalfOnSave] = useState(false);
  const [ammoTargetType, setAmmoTargetType] = useState('');   // [1H-J — 2026-05-29]: creature-type gate (e.g. 'dragon', 'undead')

  // [SUB-PLAN 1N — 2026-05-27]: HP/AC/Initiative/Saves form fields
  //   These write to canonical S1 effects[] via buildEffectsFromForm.
  //   They do NOT write to item.statModifications (S4 keyed-object).
  const [bonusHP, setBonusHP] = useState('');
  const [acBonus, setAcBonus] = useState('');
  const [initiativeBonus, setInitiativeBonus] = useState('');
  const [saves, setSaves] = useState({}); // { strength, dexterity, ..., all: number }

  // Spell assignment state
  const [itemSpells, setItemSpells] = useState([]);
  const [showSpellSelection, setShowSpellSelection] = useState(false);
  const [selectingSpellForId, setSelectingSpellForId] = useState(null);

  // Source management state
  const [saveToSource, setSaveToSource] = useState(false);
  const [selectedSource, setSelectedSource] = useState('');

  // [PHASE 10 - NOV 20, 2025]: RESTORED REMOVED FEATURES - D&D 5e item properties
  // [WHY]: These properties existed in old system, characters have items using them
  // [CRITICAL]: Cannot recreate SRD items without these properties (user requirement)
  const [rarity, setRarity] = useState(''); // Common, Uncommon, Rare, Very Rare, Legendary, Artifact
  const [attunementPrerequisite, setAttunementPrerequisite] = useState(''); // "by a spellcaster", etc.
  const [isCursed, setIsCursed] = useState(false); // Cursed item flag
  const [curseDescription, setCurseDescription] = useState(''); // Curse details
  const [resistances, setResistances] = useState([]); // Damage types character resists
  const [immunities, setImmunities] = useState([]); // Damage types character is immune to
  const [weaponProperties, setWeaponProperties] = useState([]); // Finesse, Versatile, Two-Handed, etc.
  const [normalRange, setNormalRange] = useState(''); // Normal range for ranged weapons
  const [longRange, setLongRange] = useState(''); // Long range for ranged weapons
  const [speedBonus, setSpeedBonus] = useState(''); // +10 ft movement, etc.
  const [flyingSpeed, setFlyingSpeed] = useState(''); // Flying speed granted
  const [swimmingSpeed, setSwimmingSpeed] = useState(''); // Swimming speed granted
  // [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: speed multiply + minimum fields
  const [speedMultiply, setSpeedMultiply] = useState(''); // e.g. 2 → ×2 speed (boots_of_speed)
  const [speedMinimum, setSpeedMinimum] = useState(''); // e.g. 30 → speed at least 30 ft (boots_of_striding)
  const [skillBonuses, setSkillBonuses] = useState([]); // Skill bonuses granted
  const [savingThrowBonuses, setSavingThrowBonuses] = useState([]); // Save bonuses granted
  const [conditionImmunities, setConditionImmunities] = useState([]); // Conditions immune to
  const [darkvision, setDarkvision] = useState(''); // Darkvision range
  const [blindsight, setBlindsight] = useState(''); // Blindsight range
  const [tremorsense, setTremorsense] = useState(''); // Tremorsense range
  const [truesight, setTruesight] = useState(''); // Truesight range
  const [advantageOn, setAdvantageOn] = useState([]); // Situations with advantage

  // [SUB-PLAN 1H-B — 2026-05-28]: N-array of structured effects bound to
  //   FeatureEffectsSection. Same shape as the CW creator quintet's effects
  //   authoring. On save this array merges with buildEffectsFromForm output
  //   into item.effects, identical to the EditItemDialog save path.
  const [effects, setEffects] = useState([]);

  // [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Consumable use-effect fields.
  //   Visible only for consumable items (category === 'consumables').
  //   Serialized into item.effects[] via buildEffectsFromForm — the EXACT
  //   shapes potionEffectApply.buildAppliedPotionEffect reads at drink-time.
  const [healingRoll, setHealingRoll] = useState('');             // e.g. '2d4+2'
  const [useEffectTempHP, setUseEffectTempHP] = useState('');     // number string, e.g. '10'
  // condition grant rows: [{ conditionKey, saveDC, saveAbility, recurringDamage, damageType }]
  const [grantConditions, setGrantConditions] = useState([]);
  // damage on-use: { value: '3d6', damageType: 'poison' }
  const [useEffectDamageValue, setUseEffectDamageValue] = useState('');
  const [useEffectDamageType, setUseEffectDamageType] = useState('');
  // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Runtime-extension use-effect fields.
  // feature grants: [{ target:'climbSpeed'|'flySpeed'|'waterBreathing', operation, value? }]
  const [featureGrants, setFeatureGrants] = useState([]);
  // breath weapon: { value:'4d6', damageType:'fire', saveDC:13, saveAbility:'dex', uses:3 }
  const [breathWeaponValue, setBreathWeaponValue] = useState('');
  const [breathWeaponDamageType, setBreathWeaponDamageType] = useState('fire');
  const [breathWeaponSaveDC, setBreathWeaponSaveDC] = useState('');
  const [breathWeaponSaveAbility, setBreathWeaponSaveAbility] = useState('dex');
  const [breathWeaponUses, setBreathWeaponUses] = useState('');
  // damage reduction: 'all' | '' (empty = not set)
  const [damageReductionAll, setDamageReductionAll] = useState(false);

  // Armor-specific properties (effectCalculator.calculateAC reads all four)
  const [armorType, setArmorType] = useState('light');
  const [maxDexMod, setMaxDexMod] = useState(null);
  const [stealthDisadvantage, setStealthDisadvantage] = useState(false);
  const [strengthRequirement, setStrengthRequirement] = useState(0);

  // [PHASE 5 SESSION 3]: Force re-render mechanism for reactive item creation
  // [PURPOSE]: Enables dialog to react to currency and inventory changes for real-time feedback
  // [WHY]: When adding items, users need to know:
  //   - If they can afford the item (currency changes)
  //   - If they have inventory space (inventory changes)
  //   - If purchasing is successful or needs retry
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [FIX NOV 22, 2025]: Reset all state when dialog opens to prevent previous selections persisting
  useEffect(() => {
    if (showItemDialog) {
      // Reset all state to defaults
      setCategory('weapons');
      setBaseType('dagger');
      setCustomName('');
      setCustomDesc('');
      setCustomWeight('');
      setCustomValue('');
      setCharges('');
      setMaxCharges('');
      setChargesPerDay(false);
      setRechargeType(RECHARGE_TYPES.NONE);
      setPurchaseForFree(false);
      setTreasureType('gems');
      setTreasureTier('10');
      setTreasureQuantity(1);
      setTreasureSize('tiny');
      setQuantity(1);
      setEquipSlot('');
      setRequiresAttunement(false);
      setAdvancedExpanded(false);
      setMaterial('');
      setMagicBonus('');
      setDamageDice('');
      setStatBonus('');
      setStatValue('');
      setStatModifications([]);
      // [SUB-PLAN 4L — 2026-05-28]: Reset ammunition effects fields
      setAmmoEffectsEnabled(false);
      setAmmoAttackBonus('');
      setAmmoDamageBonusFlat('');
      setAmmoDamageDice('');
      setAmmoDamageType('');
      setAmmoUseDice(false);
      setAmmoSaveEnabled(false);
      setAmmoSaveDC('');
      setAmmoSaveAbility('constitution');
      setAmmoHalfOnSave(false);
      setAmmoTargetType('');   // [1H-J]
      // [SUB-PLAN 1N — 2026-05-27]: Reset new HP/AC/initiative/saves fields
      setBonusHP('');
      setAcBonus('');
      setInitiativeBonus('');
      setSaves({});
      setItemSpells([]);
      setShowSpellSelection(false);
      setSelectingSpellForId(null);
      setSaveToSource(false);
      setSelectedSource('');
      // Reset Phase 10 properties
      setRarity('');
      setAttunementPrerequisite('');
      setIsCursed(false);
      setCurseDescription('');
      setResistances([]);
      setImmunities([]);
      setWeaponProperties([]);
      setNormalRange('');
      setLongRange('');
      setSpeedBonus('');
      setFlyingSpeed('');
      setSwimmingSpeed('');
      setSpeedMultiply('');
      setSpeedMinimum('');
      setSkillBonuses([]);
      setSavingThrowBonuses([]);
      setConditionImmunities([]);
      setDarkvision('');
      setBlindsight('');
      setTremorsense('');
      setTruesight('');
      setAdvantageOn([]);
      setEffects([]);
      setArmorType('light');
      setMaxDexMod(null);
      setStealthDisadvantage(false);
      setStrengthRequirement(0);
      // [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Reset consumable use-effect fields
      setHealingRoll('');
      setUseEffectTempHP('');
      setGrantConditions([]);
      setUseEffectDamageValue('');
      setUseEffectDamageType('');
      // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Reset Stage 2 consumable use-effect fields
      setFeatureGrants([]);
      setBreathWeaponValue('');
      setBreathWeaponDamageType('fire');
      setBreathWeaponSaveDC('');
      setBreathWeaponSaveAbility('dex');
      setBreathWeaponUses('');
      setDamageReductionAll(false);
    }
  }, [showItemDialog]);

  // [TUTORIAL]: Prompt dialog tutorial on first open
  const { promptIfNeeded } = useTutorial();
  useEffect(() => {
    if (showItemDialog) {
      promptIfNeeded('add-item');
    }
  }, [showItemDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill catalog-driven fields when baseType changes (BASE_ITEMS template
  // selected via SearchableItemSelector or category dropdown).
  useEffect(() => {
    if (!baseType || !category || category === 'packs') return;
    const base = BASE_ITEMS?.[category]?.[baseType];
    if (!base) return;

    // Armor-specific fields (existing behavior — required by
    // effectCalculator.calculateAC).
    if (category === 'armor') {
      if (base.armorType !== undefined) setArmorType(base.armorType);
      if (base.maxDexMod !== undefined) setMaxDexMod(base.maxDexMod);
      if (base.stealthDisadvantage !== undefined) setStealthDisadvantage(base.stealthDisadvantage);
      if (base.minStr !== undefined) setStrengthRequirement(base.minStr);
    }

    // [PHASE B / #004 — B1-7]: Seed equipSlot from the BASE_ITEMS template
    //   so picking a Cloak (template equipSlot='cloak'), Ring (='ring'), or
    //   Longsword (='mainhand') doesn't leave the user staring at the
    //   default "(none)" dropdown. Pre-fix the local equipSlot state was
    //   only ever assigned from user action; the catalog row's equipSlot
    //   was ignored even though BASE_ITEMS rows carry it explicitly.
    //   See `grep -n "equipSlot:" src/data/gameData.js` for the surface
    //   area covered.
    //
    //   NOTE: ItemDialog's <select> at L920+ has its own vocabulary
    //   ('ring1', 'ring2', 'feet', 'back', etc.) that diverges from both
    //   BASE_ITEMS values and the EditItemDialog dropdown. The seeded
    //   value still persists and saves correctly even when no <option>
    //   matches; the UI just renders "no selection." A canonical
    //   EQUIPMENT_SLOTS taxonomy migration is tracked as a separate
    //   Phase B vision-violation cleanup — out of scope here.
    if (base.equipSlot !== undefined) {
      setEquipSlot(base.equipSlot || '');
    } else {
      // Reset to empty when picking an item with no equipSlot — otherwise
      // a previously-seeded slot would silently persist into the new pick.
      setEquipSlot('');
    }
  }, [baseType, category]); // eslint-disable-line react-hooks/exhaustive-deps

  // [SUB-PLAN 4L — 2026-05-28]: Phase C — hydrate ammo effects form from BASE_ITEMS
  //   when the user selects an existing ammo item that already has itemEffects (e.g.
  //   arrow_plus_1). Parses the FIRST triggeredEffect row (MVP single-effect assumption).
  //   If multiple triggeredEffect rows exist, sets enabled=false so the user sees the
  //   "complex effects" read-only notice rather than a partially-populated form.
  useEffect(() => {
    if (category !== 'ammunition') {
      // Reset ammo fields when leaving the ammunition category
      setAmmoEffectsEnabled(false);
      setAmmoAttackBonus('');
      setAmmoDamageBonusFlat('');
      setAmmoDamageDice('');
      setAmmoDamageType('');
      setAmmoUseDice(false);
      setAmmoSaveEnabled(false);
      setAmmoSaveDC('');
      setAmmoSaveAbility('constitution');
      setAmmoHalfOnSave(false);
      setAmmoTargetType('');   // [1H-J]
      return;
    }
    const base = BASE_ITEMS?.ammunition?.[baseType];
    if (!base || !Array.isArray(base.itemEffects) || base.itemEffects.length === 0) {
      // No effects on this base item — leave form at defaults (user can author fresh)
      setAmmoEffectsEnabled(false);
      setAmmoAttackBonus('');
      setAmmoDamageBonusFlat('');
      setAmmoDamageDice('');
      setAmmoDamageType('');
      setAmmoUseDice(false);
      setAmmoSaveEnabled(false);
      setAmmoSaveDC('');
      setAmmoSaveAbility('constitution');
      setAmmoHalfOnSave(false);
      setAmmoTargetType('');   // [1H-J]
      return;
    }
    // Find triggeredEffect rows
    const teRows = base.itemEffects.filter(e => e.type === 'triggeredEffect');
    if (teRows.length !== 1) {
      // Multiple triggeredEffect rows — complex effects not editable via MVP form;
      // user will see the read-only notice. Leave toggle off.
      setAmmoEffectsEnabled(false);
      return;
    }
    const te = teRows[0];
    const value = te.value || {};
    const payload = Array.isArray(value.payload) ? value.payload : [];
    setAmmoEffectsEnabled(true);

    // Parse damageBonus row
    const dmgRow = payload.find(p => p.type === 'damageBonus');
    if (dmgRow) {
      if (dmgRow.operation === 'add_dice') {
        setAmmoUseDice(true);
        setAmmoDamageDice(dmgRow.value || '');
        setAmmoDamageType(dmgRow.damageType || '');
        if (dmgRow.saveDC) {
          setAmmoSaveEnabled(true);
          setAmmoSaveDC(String(dmgRow.saveDC));
          setAmmoSaveAbility(dmgRow.saveAbility || 'constitution');
          setAmmoHalfOnSave(!!dmgRow.halfOnSave);
        } else {
          setAmmoSaveEnabled(false);
          setAmmoSaveDC('');
          setAmmoSaveAbility('constitution');
          setAmmoHalfOnSave(false);
        }
      } else {
        // flat add
        setAmmoUseDice(false);
        setAmmoDamageBonusFlat(dmgRow.value !== undefined ? String(dmgRow.value) : '');
        setAmmoDamageDice('');
        setAmmoDamageType('');
        setAmmoSaveEnabled(false);
      }
    } else {
      setAmmoUseDice(false);
      setAmmoDamageBonusFlat('');
    }

    // Parse weaponAttackBonus row
    const atkRow = payload.find(p => p.type === 'weaponAttackBonus');
    if (atkRow) {
      setAmmoAttackBonus(atkRow.value !== undefined ? String(atkRow.value) : '');
    } else {
      setAmmoAttackBonus('');
    }

    // [1H-J — 2026-05-29]: Hydrate targetTypeMatch from condition (e.g. arrow_slaying → 'dragon')
    const condition = value.condition || {};
    setAmmoTargetType(condition.targetTypeMatch || '');
  }, [category, baseType]); // eslint-disable-line react-hooks/exhaustive-deps

  // [PHASE 5 SESSION 3]: Event subscriptions for reactive item dialog
  // [PURPOSE]: Subscribe to currency and inventory changes for affordability feedback
  // [WHY]: This dialog needs to show real-time updates when:
  //   - Currency changes (can afford item? need to show updated totals?)
  //   - Inventory space changes (can fit item? need to show capacity warnings?)
  //   - Items are added/removed (update suggestions or available item types)
  // [INTEGRATION]: Provides immediate feedback about transaction viability
  useEffect(() => {
    const handleDialogEvent = (eventName, data) => {
      // Force re-render to update affordability displays and capacity warnings
      forceUpdate();
    };

    // [CURRENCY_CHANGED]: Currency amount changed (affects affordability)
    const unsubscribeCurrency = systemBus.on(EVENTS.CURRENCY_CHANGED, (data) =>
      handleDialogEvent('CURRENCY_CHANGED', data));

    // [INVENTORY_CHANGED]: Inventory contents changed (affects available space)
    const unsubscribeInventory = systemBus.on(EVENTS.INVENTORY_CHANGED, (data) =>
      handleDialogEvent('INVENTORY_CHANGED', data));

    return () => {
      unsubscribeCurrency();
      unsubscribeInventory();
    };
  }, []);

  // [E-7 fix]: Build level lookup from SRD_SPELLS (343 spells, levels 0-9).
  // Previously a hardcoded ~100-spell list that missed levels 6-9 and all
  // custom spells. Now derived from the canonical source so getSpellLevel()
  // returns the correct value for every SRD spell.
  const SRD_SPELL_LEVEL_MAP = React.useMemo(() => {
    const map = {};
    Object.entries(SRD_SPELLS).forEach(([levelKey, spells]) => {
      const level = parseInt(levelKey, 10);
      spells.forEach(spell => {
        map[spell.name] = level;
      });
    });
    return map;
  }, []); // SRD_SPELLS is a static import — deps array is intentionally empty

  // Get spell level from spell name (queries SRD_SPELL_LEVEL_MAP first; falls back to 1)
  const getSpellLevel = (spellName) => {
    if (!spellName) return 1;
    const level = SRD_SPELL_LEVEL_MAP[spellName];
    return level !== undefined ? level : 1; // Default to level 1 if not found
  };
  
  // Add a new spell to the item
  const addSpell = () => {
    const newSpell = {
      id: Date.now(),
      name: '',
      level: 1,
      usesPerDay: 1,
      currentUses: 1,
      dc: null,
      // [SUB-PLAN 1H-D-ui — 2026-05-28]: per-spell charge cost for shared pool items
      // (e.g., Staff of Fire: Burning Hands = 1 charge, Fireball = 3 charges).
      // Null means the spell uses usesPerDay tracking instead of pool charges.
      costPerCast: null,
    };
    setItemSpells(prev => [...prev, newSpell]);
  };
  
  // Remove a spell from the item
  const removeSpell = (spellId) => {
    setItemSpells(prev => prev.filter(spell => spell.id !== spellId));
  };
  
  // Update a specific spell property
  const updateSpell = (spellId, property, value) => {
    setItemSpells(prev => prev.map(spell => {
      if (spell.id === spellId) {
        const updatedSpell = { ...spell, [property]: value };
        
        // Auto-update level when spell name changes
        if (property === 'name') {
          updatedSpell.level = getSpellLevel(value);
        }
        
        // Auto-update currentUses when usesPerDay changes
        if (property === 'usesPerDay') {
          updatedSpell.currentUses = value;
        }
        
        return updatedSpell;
      }
      return spell;
    }));
  };
  
  // [PHASE B / #007 — B1-8]: Confirm-before-discard guard for accidental
  //   dismissal. Pre-fix, hitting Escape, the X button, or Cancel
  //   silently wiped the user's in-progress customizations (custom name,
  //   description, weight/value overrides, equip slot, attunement,
  //   stat-mod selections, etc.). The May 5 smoke-test surfaced this as
  //   "Save with empty fields wipes the in-progress item" — the actual
  //   wipe was the dismiss path, not the submit path.
  //
  // The dirty check is derived (no separate isDirty state) — any of the
  // user-editable fields being non-default counts as in-progress work.
  // category/baseType are excluded because their defaults ('weapons' /
  // 'dagger') auto-shift when the picker fires; treating those as dirty
  // would prompt on every Cancel after a single SearchableItemSelector
  // selection.
  //
  // Save paths bypass this guard: handleSubmit calls setShowItemDialog
  // directly after a successful add, which is exactly what the user
  // wants ("Add this item, close the dialog, I'm done"). The guard is
  // for the dismiss-without-saving scenario.
  const handleCloseDialog = useCallback(async () => {
    const hasUserInput = (
      (typeof customName === 'string' && customName.trim().length > 0) ||
      (typeof customDesc === 'string' && customDesc.trim().length > 0) ||
      !!customWeight ||
      !!customValue ||
      !!charges ||
      !!maxCharges ||
      advancedExpanded ||
      itemSpells.length > 0 ||
      isCursed ||
      requiresAttunement ||
      !!material ||
      !!magicBonus ||
      !!damageDice ||
      !!equipSlot ||
      resistances.length > 0 ||
      immunities.length > 0 ||
      weaponProperties.length > 0 ||
      effects.length > 0
    );
    if (hasUserInput) {
      // [UI-CONFIRM-UNIFICATION 2026-07-04]: styled confirm via useConfirm.
      const confirmed = await confirm({
        title: 'Discard In-Progress Item?',
        message: 'Custom name, description, and any other fields you have ' +
          'entered will be lost.',
        confirmText: 'Discard',
        cancelText: 'Keep Editing',
        confirmColor: 'red',
      });
      if (!confirmed) return;
    }
    setShowItemDialog(false);
  }, [
    customName, customDesc, customWeight, customValue, charges, maxCharges,
    advancedExpanded, itemSpells, isCursed, requiresAttunement,
    material, magicBonus, damageDice, equipSlot, resistances, immunities,
    weaponProperties, effects, setShowItemDialog, confirm,
  ]);

  // [SUB-PLAN 2F kind (g) — 2026-05-25]: Rich onChange for armorType picker.
  //   Extracted from inline onChange (Rule 30c helper-extraction precondition).
  //   Sets armorType + derives sensible defaults for maxDexMod and stealthDisadvantage.
  function handleArmorTypeChange(t) {
    setArmorType(t);
    if (t === 'light') { setMaxDexMod(null); setStealthDisadvantage(false); }
    else if (t === 'medium') { setMaxDexMod(2); setStealthDisadvantage(false); }
    else if (t === 'heavy') { setMaxDexMod(0); setStealthDisadvantage(true); }
    else if (t === 'shield') { setMaxDexMod(null); setStealthDisadvantage(false); }
  }

  // [PHASE B / #007 — B1-8 → wave 2d 2026-07-09]: Escape's guarded close path
  //   (handleCloseDialog) is now registered by MobileModal internally via its
  //   onClose prop — the dialog's own useModalEscape call was removed with the
  //   hand-rolled shell.

  // [SUB-PLAN 4L — 2026-05-28]: Build itemEffects[] from the ammo effects form state.
  //   Storage shape is identical to SRD ammo rows (gameData.js L502-581) so
  //   CW-authored ammo co-exists with SRD ammo at runtime without special-casing.
  //   Called from the main handleSubmit path only when category==='ammunition'.
  //
  // Phase B — save handler. Phase C (load/hydrate) lives in the ammo section render.
  //
  // Returns undefined when toggle is off or no meaningful fields are populated,
  // so addNewItem won't persist an empty itemEffects array.
  function buildAmmoItemEffects(itemName, ammoTypeValue, targetType) {
    if (!ammoEffectsEnabled) return undefined;
    const payload = [];

    // Flat damage bonus row
    if (!ammoUseDice) {
      const flatVal = parseInt(ammoDamageBonusFlat, 10);
      if (Number.isFinite(flatVal) && flatVal !== 0) {
        payload.push({ type: 'damageBonus', operation: 'add', value: flatVal });
      }
    } else {
      // Dice damage bonus row (with optional save)
      const diceStr = ammoDamageDice.trim();
      if (diceStr) {
        const diceRow = {
          type: 'damageBonus',
          operation: 'add_dice',
          value: diceStr,
        };
        if (ammoDamageType) diceRow.damageType = ammoDamageType;
        if (ammoSaveEnabled) {
          const dc = parseInt(ammoSaveDC, 10);
          if (Number.isFinite(dc) && dc > 0) diceRow.saveDC = dc;
          if (ammoSaveAbility) diceRow.saveAbility = ammoSaveAbility;
          diceRow.halfOnSave = !!ammoHalfOnSave;
        }
        payload.push(diceRow);
      }
    }

    // Attack bonus row (only when non-zero)
    const atkVal = parseInt(ammoAttackBonus, 10);
    if (Number.isFinite(atkVal) && atkVal !== 0) {
      payload.push({ type: 'weaponAttackBonus', operation: 'add', value: atkVal });
    }

    if (payload.length === 0) return undefined;

    return [
      {
        type: 'triggeredEffect',
        operation: 'grant',
        value: {
          triggerId: 'on_hit',
          condition: { ammoTypeMatch: ammoTypeValue, ...(targetType ? { targetTypeMatch: targetType.trim().toLowerCase() } : {}) },
          payload,
          sourceLabel: itemName,
        },
      },
    ];
  }

  // [UI-CONFIRM-UNIFICATION 2026-07-04]: async — addNewItem (App.jsx) now
  // awaits a styled weight-limit confirm, so its boolean result must be
  // awaited wherever it is consumed (a bare Promise is always truthy).
  const handleSubmit = async () => {
    if (category === 'packs') {
      // Handle starter pack selection
      addStarterPack(baseType);
      setShowItemDialog(false);
      setCustomName('');
      setCustomDesc('');
      setCustomWeight('');
      setCustomValue('');
      setCharges('');
      setMaxCharges('');
      setChargesPerDay(false);
      setRechargeType(RECHARGE_TYPES.NONE);
      setPurchaseForFree(false);
      setItemSpells([]);
      setEquipSlot('');
      setRequiresAttunement(false);
      return;
    }

    // Handle multiple treasures (gems/art objects ONLY)
    // [PHASE 10 - NOV 20, 2025]: Added restored properties to multiple treasures path
    if ((category === 'gems' || category === 'art_objects') && treasureQuantity > 1) {
      let allAdded = true;
      for (let i = 0; i < treasureQuantity; i++) {
        const itemData = {
          category,
          baseType,
          name: customName || `${baseType.replace(/_/g, ' ')} ${i + 1}`,
          description: customDesc,
          weight: customWeight ? parseFloat(customWeight) : undefined,
          value: customValue ? parseFloat(customValue) : undefined,
          purchaseForFree,
          equipSlot: equipSlot || null,
          requiresAttunement,
          charges: charges ? parseInt(charges) : undefined,
          maxCharges: maxCharges ? parseInt(maxCharges) : undefined,
          rechargeType: rechargeType || RECHARGE_TYPES.NONE,
          spells: itemSpells.length > 0 ? itemSpells.map(spell => ({
            name: spell.name,
            level: spell.level,
            usesPerDay: spell.usesPerDay,
            currentUses: spell.currentUses,
            dc: spell.dc,
            // [SUB-PLAN 1H-D-ui]: per-spell charge cost for shared pool items; omit when null
            ...(spell.costPerCast != null ? { costPerCast: spell.costPerCast } : {}),
          })) : undefined,
          // [PHASE 10]: RESTORED D&D 5e PROPERTIES (same as single item path)
          rarity: rarity || undefined,
          attunementPrerequisite: (requiresAttunement && attunementPrerequisite) ? attunementPrerequisite : undefined,
          isCursed: isCursed || undefined,
          curseDescription: (isCursed && curseDescription) ? curseDescription : undefined,
          resistances: resistances.length > 0 ? resistances : undefined,
          immunities: immunities.length > 0 ? immunities : undefined,
          weaponProperties: weaponProperties.length > 0 ? weaponProperties : undefined,
          normalRange: normalRange ? parseInt(normalRange) : undefined,
          longRange: longRange ? parseInt(longRange) : undefined,
          speedBonus: speedBonus ? parseInt(speedBonus) : undefined,
          flyingSpeed: flyingSpeed ? parseInt(flyingSpeed) : undefined,
          swimmingSpeed: swimmingSpeed ? parseInt(swimmingSpeed) : undefined,
          // [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: speed multiply + minimum flat storage
          speedMultiply: speedMultiply ? parseFloat(speedMultiply) : undefined,
          speedMinimum: speedMinimum ? parseInt(speedMinimum) : undefined,
          skillBonuses: skillBonuses.length > 0 ? skillBonuses : undefined,
          savingThrowBonuses: savingThrowBonuses.length > 0 ? savingThrowBonuses : undefined,
          conditionImmunities: conditionImmunities.length > 0 ? conditionImmunities : undefined,
          darkvision: darkvision ? parseInt(darkvision) : undefined,
          blindsight: blindsight ? parseInt(blindsight) : undefined,
          tremorsense: tremorsense ? parseInt(tremorsense) : undefined,
          truesight: truesight ? parseInt(truesight) : undefined,
          advantageOn: advantageOn.length > 0 ? advantageOn : undefined,
          // Armor fields — required by effectCalculator.calculateAC
          armorType: category === 'armor' ? armorType : undefined,
          maxDexMod: category === 'armor' ? maxDexMod : undefined,
          stealthDisadvantage: category === 'armor' ? stealthDisadvantage : undefined,
          strengthRequirement: category === 'armor' ? (strengthRequirement || 0) : undefined
        };
        if (!(await addNewItem(itemData))) {
          allAdded = false;
          break;
        }
      }
      
      if (allAdded) {
        setShowItemDialog(false);
        // Reset all form fields
        setCustomName('');
        setCustomDesc('');
        setCustomWeight('');
        setCustomValue('');
        setPurchaseForFree(false);
        setTreasureQuantity(1);
        // Reset advanced properties
        setAdvancedExpanded(false);
        setMaterial('');
        setMagicBonus('');
        setDamageDice('');
        setItemSpells([]);
        setEquipSlot('');
        setRequiresAttunement(false);
        // [PHASE 10]: Reset restored D&D 5e properties
        setRarity('');
        setAttunementPrerequisite('');
        setIsCursed(false);
        setCurseDescription('');
        setResistances([]);
        setImmunities([]);
        setWeaponProperties([]);
        setNormalRange('');
        setLongRange('');
        setSpeedBonus('');
        setFlyingSpeed('');
        setSwimmingSpeed('');
        setSpeedMultiply('');
        setSpeedMinimum('');
        setSkillBonuses([]);
        setSavingThrowBonuses([]);
        setConditionImmunities([]);
        setDarkvision('');
        setBlindsight('');
        setTremorsense('');
        setTruesight('');
        setAdvantageOn([]);
        setArmorType('light');
        setMaxDexMod(null);
        setStealthDisadvantage(false);
        setStrengthRequirement(0);
      }
      return;
    }

    // Handle trade goods and spell components with quantity
    // [BUG FIX 2025-10-10]: Create ONE item with quantity instead of multiple items
    // [WHY]: User reported "creating 50 individual '1 ft' items instead of one '50 ft rope' item"
    // [FIX]: Create single item with quantity field, InventoryItem will handle weight/value multiplication
    if (category === 'trade_goods' || category === 'spell_components') {
      const baseItem = BASE_ITEMS[category]?.[baseType];
      if (!baseItem) return;

      // Unit value and weight (will be multiplied by InventoryItem class)
      const unitValue = customValue ? parseFloat(customValue) : baseItem.baseValue;
      const unitWeight = customWeight ? parseFloat(customWeight) : baseItem.weight;

      // Create ONE item with quantity
      const itemData = {
        category,
        baseType,
        name: customName || baseType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        description: customDesc || baseItem.description,
        weight: unitWeight,
        value: unitValue,
        quantity: quantity, // InventoryItem will multiply weight/value by this
        purchaseForFree,
        charges: charges ? parseInt(charges) : undefined,
        maxCharges: maxCharges ? parseInt(maxCharges) : undefined,
        rechargeType: rechargeType || RECHARGE_TYPES.NONE,
        // Special properties for spell components
        providesFocus: baseItem.providesFocus,
        providesBasicComponents: baseItem.providesBasicComponents,
        focusType: baseItem.focusType,
        containerType: baseItem.containerType,
        isContainer: baseItem.isContainer,
        containerCapacity: baseItem.containerCapacity
      };

      if (await addNewItem(itemData)) {
        setShowItemDialog(false);
        setCustomName('');
        setCustomDesc('');
        setCustomWeight('');
        setCustomValue('');
        setQuantity(1);
        setPurchaseForFree(false);
      }
      return;
    }

    // Generate enhanced name based on properties
    // [FIX NOV 22, 2025]: Properly capitalize item names
    // [FIX NOV 28, 2025]: Magic bonus goes BEFORE name ("+1 Longsword" not "Longsword +1")
    // [APR 21 2026 FIX]: If user typed a custom name, use it AS-IS. Prior code
    // auto-prefixed "+1 Mithral Slice" even when the user explicitly named the
    // item "Slice" and had no way to remove the prefix. Magical bonus and
    // material are already stored as structured fields (magicalBonus, material)
    // and rendered as badges — no need to jam them into the display name too.
    // Only auto-build the prefix when the user didn't set a custom name.
    const capitalizedBaseType = baseType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    let enhancedName;
    if (customName) {
      enhancedName = customName;
    } else if (material || magicBonus) {
      let prefix = '';
      if (magicBonus) prefix += `+${magicBonus} `;
      if (material) prefix += material.charAt(0).toUpperCase() + material.slice(1).replace('_', ' ') + ' ';
      enhancedName = prefix + capitalizedBaseType;
    } else {
      enhancedName = capitalizedBaseType;
    }
    
    // Handle single item
    // [PHASE 10 - NOV 20, 2025]: Added all restored D&D 5e item properties
    // [LANE-C DEFAULTS — 2026-06-13]: Resolve charges default (current = max when
    //   user sets only maxCharges and leaves current blank). Mirrors EditItemDialog's
    //   resolveCurrentCharges wiring (Sub-plan 1C item 1 / 2026-05-23). The
    //   InventoryItem constructor already does this at build time (App.jsx:387);
    //   applying it here makes itemData self-consistent before the constructor runs.
    const _resolvedMaxCharges = maxCharges ? parseInt(maxCharges, 10) : undefined;
    const _resolvedCharges = resolveCurrentCharges(
      charges !== '' ? charges : undefined,
      _resolvedMaxCharges
    );
    // [LANE-C DEFAULTS — 2026-06-13]: Default ammunition to stackable:true / maxStack:20
    //   when the authored item has no explicit stackable flag. For BASE_ITEMS ammo rows
    //   the constructor already inherits stackable+maxStack from the base row; this
    //   default covers novel CW-authored ammo types not found in BASE_ITEMS (baseItem={}).
    //   Rule 9: only set the default when the author has not explicitly provided a value.
    const _ammoStackDefaults = (category === 'ammunition')
      ? { stackable: true, maxStack: 20 }
      : {};
    const itemData = {
      category,
      baseType,
      name: enhancedName,
      description: customDesc,
      weight: customWeight ? parseFloat(customWeight) : undefined,
      value: customValue ? parseFloat(customValue) : undefined,
      purchaseForFree,
      equipSlot: equipSlot || null,
      requiresAttunement,
      charges: _resolvedCharges ?? undefined,
      maxCharges: _resolvedMaxCharges ?? undefined,
      rechargeType: rechargeType || RECHARGE_TYPES.NONE,
      ..._ammoStackDefaults,
      // Advanced properties
      material: material || undefined,
      magicalBonus: magicBonus ? parseInt(magicBonus) : undefined,
      damageDice: damageDice || undefined,
      // [STONE 4 — 2026-05-26]: write canonical array + populate legacy mirror from first row
      //   for backward-compat. Downstream code reading only statBonus/statValue continues
      //   to see the first row; new code (itemEffectsRegistration) reads the array.
      statModifications: statModifications.length > 0 ? statModifications : undefined,
      statBonus: (statModifications[0]?.target) || undefined,
      statValue: (statModifications[0]?.value != null ? statModifications[0].value : undefined),
      // [STONES 5-6 — 2026-05-26]: Stamp canonical S1 effects array so
      //   buildItemRegistrationsFromInventory → effectRegistry picks up ALL
      //   user-authored passive effects (ability mods, resistances, senses,
      //   speed). Rule 31 trace: .claude/findings/2026-05-26-datamodel-item-effects-4e7d12.md.
      //   Legacy S2 mirrors (statBonus/statValue) are kept above for path-3
      //   backward-compat (InventoryContext.applyItemBonus). Deprecation of
      //   path-3 is a separate follow-up sub-plan after both dialogs migrate.
      // [SUB-PLAN 1N — 2026-05-27]: Mirror flat properties for ergonomic round-trip.
      //   NOT for runtime reads — runtime uses effects[] via effectRegistry (path-1).
      //   DO NOT add these to statModifications keyed-object (S4 deepening blocked).
      bonusHP: bonusHP !== '' ? Number(bonusHP) : undefined,
      acBonus: acBonus !== '' ? Number(acBonus) : undefined,
      initiativeBonus: initiativeBonus !== '' ? Number(initiativeBonus) : undefined,
      saves: Object.keys(saves).length > 0 ? saves : undefined,
      ...(() => {
        const builtEffects = buildEffectsFromForm({
          statModifications,
          resistances,
          immunities,
          conditionImmunities,
          darkvision: darkvision ? parseInt(darkvision) : undefined,
          blindsight: blindsight ? parseInt(blindsight) : undefined,
          tremorsense: tremorsense ? parseInt(tremorsense) : undefined,
          truesight: truesight ? parseInt(truesight) : undefined,
          speedBonus: speedBonus ? parseInt(speedBonus) : undefined,
          flyingSpeed: flyingSpeed ? parseInt(flyingSpeed) : undefined,
          swimmingSpeed: swimmingSpeed ? parseInt(swimmingSpeed) : undefined,
          // [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: speed multiply + minimum to emitter
          speedMultiply: speedMultiply ? parseFloat(speedMultiply) : undefined,
          speedMinimum: speedMinimum ? parseInt(speedMinimum) : undefined,
          advantageOn,
          // [SUB-PLAN 1K — 2026-05-27]: Pass new fields to emitter
          bonusHP: bonusHP !== '' ? bonusHP : undefined,
          acBonus: acBonus !== '' ? acBonus : undefined,
          initiativeBonus: initiativeBonus !== '' ? initiativeBonus : undefined,
          saves,
          // [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Consumable use-effects
          healingRoll: healingRoll || undefined,
          tempHP: useEffectTempHP !== '' ? useEffectTempHP : undefined,
          grantConditions: grantConditions.length > 0 ? grantConditions : undefined,
          useEffectDamage: (useEffectDamageValue.trim())
            ? { value: useEffectDamageValue.trim(), damageType: useEffectDamageType || undefined }
            : undefined,
          // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Runtime-extension use-effects
          featureGrants: featureGrants.length > 0 ? featureGrants : undefined,
          breathWeapon: breathWeaponValue.trim()
            ? {
                value: breathWeaponValue.trim(),
                damageType: breathWeaponDamageType || 'fire',
                saveDC: breathWeaponSaveDC !== '' ? breathWeaponSaveDC : undefined,
                saveAbility: breathWeaponSaveAbility || 'dex',
                uses: breathWeaponUses !== '' ? breathWeaponUses : undefined,
              }
            : undefined,
          damageReduction: damageReductionAll ? 'all' : undefined,
        });
        // [SUB-PLAN 1H-B — 2026-05-28]: Merge FeatureEffectsSection-authored
        //   effects (fromSection) with form-derived effects (fromForm), identical
        //   to EditItemDialog's handleSave merge (Stones 5-6 / Phase B #026).
        //   Merge order: fromSection first (verbatim user-authored rows), then
        //   fromForm (ability mods, resistances, senses, speed).
        const fromSection = Array.isArray(effects) ? [...effects] : [];
        const merged = [...fromSection, ...builtEffects];
        return { effects: merged.length > 0 ? merged : undefined };
      })(),
      // [SUB-PLAN 4L — 2026-05-28]: Ammo on-hit itemEffects. Only emitted when
      //   category === 'ammunition' AND toggle is on AND payload is non-empty.
      //   ammoType is read from BASE_ITEMS row so ammoTypeMatch is always auto-derived.
      ...((() => {
        if (category !== 'ammunition') return {};
        const baseAmmoItem = BASE_ITEMS?.ammunition?.[baseType];
        const resolvedAmmoType = baseAmmoItem?.ammoType || '';
        const built = buildAmmoItemEffects(enhancedName, resolvedAmmoType, ammoTargetType);
        return built ? { itemEffects: built } : {};
      })()),
      spells: itemSpells.length > 0 ? itemSpells.map(spell => ({
        name: spell.name,
        usesPerDay: spell.usesPerDay,
        currentUses: spell.usesPerDay,
        // [SUB-PLAN 1H-D-ui]: per-spell charge cost for shared pool items; omit when null
        ...(spell.costPerCast != null ? { costPerCast: spell.costPerCast } : {}),
      })) : undefined,
      // [PHASE 10]: RESTORED D&D 5e PROPERTIES
      // [WHY]: These properties existed in old system and are required for SRD item recreation
      rarity: rarity || undefined,
      attunementPrerequisite: (requiresAttunement && attunementPrerequisite) ? attunementPrerequisite : undefined,
      isCursed: isCursed || undefined,
      curseDescription: (isCursed && curseDescription) ? curseDescription : undefined,
      resistances: resistances.length > 0 ? resistances : undefined,
      immunities: immunities.length > 0 ? immunities : undefined,
      weaponProperties: weaponProperties.length > 0 ? weaponProperties : undefined,
      normalRange: normalRange ? parseInt(normalRange) : undefined,
      longRange: longRange ? parseInt(longRange) : undefined,
      speedBonus: speedBonus ? parseInt(speedBonus) : undefined,
      flyingSpeed: flyingSpeed ? parseInt(flyingSpeed) : undefined,
      swimmingSpeed: swimmingSpeed ? parseInt(swimmingSpeed) : undefined,
      // [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: speed multiply + minimum flat storage (for round-trip hydration)
      speedMultiply: speedMultiply ? parseFloat(speedMultiply) : undefined,
      speedMinimum: speedMinimum ? parseInt(speedMinimum) : undefined,
      skillBonuses: skillBonuses.length > 0 ? skillBonuses : undefined,
      savingThrowBonuses: savingThrowBonuses.length > 0 ? savingThrowBonuses : undefined,
      conditionImmunities: conditionImmunities.length > 0 ? conditionImmunities : undefined,
      darkvision: darkvision ? parseInt(darkvision) : undefined,
      blindsight: blindsight ? parseInt(blindsight) : undefined,
      tremorsense: tremorsense ? parseInt(tremorsense) : undefined,
      truesight: truesight ? parseInt(truesight) : undefined,
      advantageOn: advantageOn.length > 0 ? advantageOn : undefined,
      // Armor fields — required by effectCalculator.calculateAC
      armorType: category === 'armor' ? armorType : undefined,
      maxDexMod: category === 'armor' ? maxDexMod : undefined,
      stealthDisadvantage: category === 'armor' ? stealthDisadvantage : undefined,
      strengthRequirement: category === 'armor' ? (strengthRequirement || 0) : undefined
    };

    if (await addNewItem(itemData)) {
      // Save to source if option is selected
      if (saveToSource && selectedSource) {
        try {
          addItemToSource(selectedSource, itemData);
        } catch (e) {
          console.error('Failed to save item to source:', e);
        }
      }

      setShowItemDialog(false);
      setCustomName('');
      setCustomDesc('');
      setCustomWeight('');
      setCustomValue('');
      setPurchaseForFree(false);
      setTreasureQuantity(1);
      // Reset advanced properties
      setAdvancedExpanded(false);
      setMaterial('');
      setMagicBonus('');
      setDamageDice('');
      setStatBonus('');
      setStatValue('');
      setStatModifications([]);
      setItemSpells([]);
      setEquipSlot('');
      setRequiresAttunement(false);
      // [PHASE 10]: Reset restored D&D 5e properties
      setRarity('');
      setAttunementPrerequisite('');
      setIsCursed(false);
      setCurseDescription('');
      setResistances([]);
      setImmunities([]);
      setWeaponProperties([]);
      setNormalRange('');
      setLongRange('');
      setSpeedBonus('');
      setFlyingSpeed('');
      setSwimmingSpeed('');
      setSpeedMultiply('');
      setSpeedMinimum('');
      setSkillBonuses([]);
      setSavingThrowBonuses([]);
      setConditionImmunities([]);
      setDarkvision('');
      setBlindsight('');
      setTremorsense('');
      setTruesight('');
      setAdvantageOn([]);
      setArmorType('light');
      setMaxDexMod(null);
      setStealthDisadvantage(false);
      setStrengthRequirement(0);
      // [SUB-PLAN 4L — 2026-05-28]: Reset ammo effects fields
      setAmmoEffectsEnabled(false);
      setAmmoAttackBonus('');
      setAmmoDamageBonusFlat('');
      setAmmoDamageDice('');
      setAmmoDamageType('');
      setAmmoUseDice(false);
      setAmmoSaveEnabled(false);
      setAmmoSaveDC('');
      setAmmoSaveAbility('constitution');
      setAmmoHalfOnSave(false);
      setAmmoTargetType('');   // [1H-J]
      // [SUB-PLAN 1H-B — 2026-05-28]: Reset FeatureEffectsSection effects
      setEffects([]);
      // [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Reset consumable use-effect fields
      setHealingRoll('');
      setUseEffectTempHP('');
      setGrantConditions([]);
      setUseEffectDamageValue('');
      setUseEffectDamageType('');
      // [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Reset Stage 2 consumable use-effect fields
      setFeatureGrants([]);
      setBreathWeaponValue('');
      setBreathWeaponDamageType('fire');
      setBreathWeaponSaveDC('');
      setBreathWeaponSaveAbility('dex');
      setBreathWeaponUses('');
      setDamageReductionAll(false);
      // Reset source options
      setSaveToSource(false);
      setSelectedSource('');
    }
  };

  if (!showItemDialog) return null;

  return (
    <>
    {/* [UI-MOBILEMODAL-ADOPTION wave 2d — 2026-07-09]: main shell wraps MobileModal.
        onClose = handleCloseDialog keeps the confirm-before-discard guard on Escape,
        X, and (unchanged) the footer Cancel. Footer actions stay in body per the wave
        convention. The nested z-[60] spell sub-dialog below stays hand-rolled +
        button-close-only (no Escape) so Escape semantics are unchanged: it still
        routes to the MAIN dialog's guarded close, exactly as before migration. */}
    <MobileModal
      isOpen
      onClose={handleCloseDialog}
      title={isFindMode ? 'Add Item' : 'Add New Item'}
      showSaveButton={false}
    >
      <div>

        {/* [PHASE 5 - NOV 19, 2025]: Item Selection */}
        {/* [INTEGRATION]: SearchableItemSelector provides fuzzy search, recent items, and favorites */}

        {/* Show selected item info */}
        {baseType && (
          <div className="mb-4 p-3 bg-gray-800 rounded-lg border border-gray-700" data-tutorial="add-item-options">
            <div className="text-xs text-gray-400 mb-1">Currently Selected:</div>
            <div className="flex items-center gap-2">
              {category === 'packs' ? (
                <>
                  <span className="text-lg">📦</span>
                  <span className="font-medium">{STARTER_PACKS[baseType]?.name}</span>
                  <span className="text-sm text-gray-400">({formatCurrency(STARTER_PACKS[baseType]?.cost || 0)})</span>
                </>
              ) : (
                <>
                  <span className="text-lg">{BASE_ITEMS[category]?.[baseType]?.icon || '📄'}</span>
                  <span className="font-medium">
                    {baseType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </span>
                  <span className="text-sm text-gray-400">
                    ({BASE_ITEMS[category]?.[baseType]?.size?.[0]}x{BASE_ITEMS[category]?.[baseType]?.size?.[1]},
                    {BASE_ITEMS[category]?.[baseType]?.weight}lb)
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* [PHASE 5 - NOV 19, 2025]: SearchableItemSelector restored for better UX */}
        {/* [PURPOSE]: Provides fuzzy search, recent items, and favorites for item selection */}
        {/* [WHY]: Dropdown navigation was slow for 200+ items - search is faster */}
        <SearchableItemSelector
          BASE_ITEMS={BASE_ITEMS}
          STARTER_PACKS={STARTER_PACKS}
          selectedCategory={category}
          selectedItem={baseType}
          onCategoryChange={(cat) => {
            setCategory(cat);
            // Auto-select first item when category changes
            if (cat === 'packs') {
              setBaseType(Object.keys(STARTER_PACKS)[0]);
            } else {
              setBaseType(Object.keys(BASE_ITEMS[cat])[0]);
            }
          }}
          onItemChange={(item) => setBaseType(item)}
          showPacks={true}
          /* [2026-05-25]: In find mode, render the category browser INLINE
             (no popup, no Stage-2 modal) and show full item properties on
             each row so the user can browse + pick without typing. */
          findMode={isFindMode}
        />
        
        {/* Magical Container Information */}
        {category === 'containers' && BASE_ITEMS[category]?.[baseType]?.extradimensional && (
          <div className="mb-4 p-3 bg-gradient-to-r from-purple-900 to-indigo-900 rounded border border-purple-500">
            <h4 className="text-sm font-bold text-purple-300 mb-2 flex items-center">
              ✨ Magical Container Properties
            </h4>
            <div className="text-xs text-purple-200 space-y-1">
              <p><span className="font-semibold">📦 Interior Space:</span> {BASE_ITEMS[category][baseType].containerSlots[0]}x{BASE_ITEMS[category][baseType].containerSlots[1]} slots</p>
              <p><span className="font-semibold">⚖️ Weight Reduction:</span> {(BASE_ITEMS[category][baseType].weightReduction * 100).toFixed(0)}% (contents weigh {100 - (BASE_ITEMS[category][baseType].weightReduction * 100).toFixed(0)}% of normal)</p>
              <p><span className="font-semibold">💪 Max Capacity:</span> {BASE_ITEMS[category][baseType].maxWeight} lbs</p>
              {BASE_ITEMS[category][baseType].description && (
                <p className="italic mt-2 text-purple-100">{BASE_ITEMS[category][baseType].description}</p>
              )}
              {BASE_ITEMS[category][baseType].cursed && (
                <p className="text-red-400 font-semibold mt-2">⚠️ WARNING: This container is cursed!</p>
              )}
            </div>
          </div>
        )}
        
        {/* Quantity Field for Trade Goods and Spell Components */}
        {(category === 'trade_goods' || category === 'spell_components') && (
          <div className="mb-4 p-3 bg-gradient-to-r from-green-900 to-blue-900 rounded">
            <h4 className="text-sm font-bold text-green-300 mb-3">
              {category === 'trade_goods' ? '📦 Trade Goods' : '🧪 Spell Components'}
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-green-200 mb-1">Quantity to Add</label>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full p-2 bg-gray-800 text-white rounded"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Each: {formatCurrency(customValue || BASE_ITEMS[category]?.[baseType]?.baseValue || 0)}
                </p>
              </div>
              <div>
                <label className="block text-xs text-green-200 mb-1">Total Value</label>
                <div className="p-2 bg-gray-700 rounded text-yellow-400 font-bold">
                  {formatCurrency((customValue || BASE_ITEMS[category]?.[baseType]?.baseValue || 0) * quantity)}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Total Weight: {((customWeight || BASE_ITEMS[category]?.[baseType]?.weight || 0) * quantity).toFixed(2)}lb
                </p>
              </div>
            </div>
            {category === 'trade_goods' && (
              <div className="mt-2 p-2 bg-blue-800 bg-opacity-30 rounded">
                <p className="text-xs text-blue-200">
                  📦 Creates 1 item with quantity {quantity} (e.g., "50 ft rope")
                </p>
              </div>
            )}
            {category === 'spell_components' && BASE_ITEMS[category]?.[baseType]?.providesFocus && (
              <div className="mt-3 p-2 bg-purple-800 bg-opacity-50 rounded">
                <p className="text-xs text-purple-200">
                  ✨ This focus replaces non-costly material components for spells
                </p>
              </div>
            )}
            {category === 'spell_components' && BASE_ITEMS[category]?.[baseType]?.providesBasicComponents && (
              <div className="mt-3 p-2 bg-blue-800 bg-opacity-50 rounded">
                <p className="text-xs text-blue-200">
                  👝 Component pouch provides materials without specific costs
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Enhanced Treasure Selection System - ONLY for gems and art objects */}
        {(category === 'gems' || category === 'art_objects') && (
          <div className="mb-4 p-3 bg-gradient-to-r from-amber-900 to-purple-900 rounded">
            <h4 className="text-sm font-bold text-amber-300 mb-3">
              {category === 'gems' ? '💎 Gem Treasury' : '🏛️ Art & Antiquities'}
            </h4>
            
            {/* Remove redundant treasure type selector - category is already selected */}

            {/* Value Tier & Size Selection */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div>
                <label className="block text-xs text-amber-200 mb-1">Gold Value Filter</label>
                <select 
                  value={treasureTier}
                  onChange={(e) => {
                    setTreasureTier(e.target.value);
                    // Find first item in this tier
                    const itemsOfTier = Object.entries(BASE_ITEMS[category] || {}).filter(([_, item]) => 
                      item.baseValue === parseInt(e.target.value));
                    if (itemsOfTier.length > 0) {
                      setBaseType(itemsOfTier[0][0]);
                    }
                  }}
                  className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                  title="Filter items by their exact gold piece value"
                >
                  {/* Generate options based on actual values in the category */}
                  {(() => {
                    const values = [...new Set(Object.values(BASE_ITEMS[category] || {}).map(item => item.baseValue))].sort((a, b) => a - b);
                    return values.map(value => (
                      <option key={value} value={value}>
                        {formatCurrency(value)} items
                      </option>
                    ));
                  })()}
                </select>
              </div>
              
              <div>
                <label className="block text-xs text-amber-200 mb-1">Physical Size</label>
                <select 
                  value={treasureSize}
                  onChange={(e) => setTreasureSize(e.target.value)}
                  className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                >
                  <option value="tiny">💍 Tiny (Ring/Gem)</option>
                  <option value="small">🧿 Small (Pendant)</option>
                  <option value="medium">🏺 Medium (Chalice)</option>
                  <option value="large">👑 Large (Crown)</option>
                  <option value="huge">⚱️ Huge (Statue)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-amber-200 mb-1">Quantity</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={treasureQuantity}
                  onChange={(e) => setTreasureQuantity(parseInt(e.target.value) || 1)}
                  className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                />
              </div>
            </div>

            {/* Item Selection */}
            <div className="mb-3">
              <label className="block text-xs text-amber-200 mb-1">Select Specific Item</label>
              <div className="text-xs text-gray-400 mb-1">Showing items valued at {formatCurrency(treasureTier)}</div>
              <select
                value={baseType}
                onChange={(e) => setBaseType(e.target.value)}
                className="w-full p-2 bg-gray-800 text-white rounded text-sm border border-amber-600"
              >
                {Object.entries(BASE_ITEMS[category] || {})
                  .filter(([_, item]) => item.baseValue === parseInt(treasureTier))
                  .map(([key, item]) => (
                    <option key={key} value={key}>
                      {item.icon} {key.replace(/_/g, ' ')} - {formatCurrency(item.baseValue)}
                    </option>
                  ))}
              </select>
            </div>
            
            {/* Quick Value Filter Buttons - Same as dropdown above but as buttons for convenience */}
            <div className="mb-3">
              <label className="block text-xs text-amber-200 mb-1">Quick Value Selection (same as dropdown above)</label>
              <div className="text-xs text-gray-400 mb-1">Click to quickly jump to common value tiers</div>
              <div className="flex flex-wrap gap-1">
                {[
                  { tier: '10', name: '10gp', color: 'bg-gray-600', desc: 'Ornamental' },
                  { tier: '50', name: '50gp', color: 'bg-blue-600', desc: 'Semi-precious' },
                  { tier: '100', name: '100gp', color: 'bg-green-600', desc: 'Precious' },
                  { tier: '500', name: '500gp', color: 'bg-purple-600', desc: 'Rare' },
                  { tier: '1000', name: '1000gp', color: 'bg-pink-600', desc: 'Very Rare' },
                  { tier: '5000', name: '5000gp', color: 'bg-yellow-600', desc: 'Legendary' }
                ].map(({ tier, name, color, desc }) => (
                  <button
                    key={tier}
                    onClick={() => {
                      setTreasureTier(tier);
                      // Find items in this tier
                      const itemsOfTier = Object.entries(BASE_ITEMS[category] || {}).filter(([_, item]) => 
                        item.baseValue === parseInt(tier));
                      if (itemsOfTier.length > 0 && !itemsOfTier.find(([key]) => key === baseType)) {
                        // Only change selection if current item isn't in this tier
                        setBaseType(itemsOfTier[0][0]);
                      }
                    }}
                    className={`px-2 py-1 rounded text-xs text-white transition-colors ${color} hover:opacity-80 ${
                      treasureTier === tier ? 'ring-2 ring-amber-400' : ''
                    }`}
                    title={`Show ${desc} items worth ${name}`}
                  >
                    {name}
                  </button>
                ))}
              </div>
            </div>

            {/* Selection Summary */}
            <div className="bg-gray-800 p-2 rounded border border-amber-600">
              <div className="text-xs text-amber-300 space-y-1">
                <div className="flex justify-between">
                  <span>Selected:</span>
                  <span className="font-mono">
                    {BASE_ITEMS[treasureType][baseType]?.icon} {baseType?.replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Unit Value:</span>
                  <span className="font-mono text-green-400">
                    {formatCurrency(BASE_ITEMS[treasureType][baseType]?.baseValue || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Value:</span>
                  <span className="font-mono text-yellow-400 font-bold">
                    {formatCurrency((BASE_ITEMS[treasureType][baseType]?.baseValue || 0) * treasureQuantity)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Total Weight:</span>
                  <span className="font-mono">
                    {((BASE_ITEMS[treasureType][baseType]?.weight || 0) * treasureQuantity).toFixed(1)} lbs
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* [2026-05-25]: Edit fields (Custom Name/Description/Weight/Value)
            are hidden in find mode — Inventory's Add Item is a picker, not
            an editor. The customN state stays at '' so addNewItem falls
            back to BASE_ITEMS defaults on submit. */}
        {!isFindMode && (
          <>
        <div className="mb-4" data-tutorial="add-item-customize">
          <label className="block text-sm text-gray-300 mb-1">Custom Name</label>
          <input
            type="text"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            placeholder={baseType}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">Description</label>
          <textarea
            value={customDesc}
            onChange={(e) => setCustomDesc(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded h-20"
            placeholder="A mysterious artifact..."
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">
            Weight (lbs) - Default: {category !== 'packs' && BASE_ITEMS[category] && BASE_ITEMS[category][baseType] ? BASE_ITEMS[category][baseType].weight : 'N/A'}
          </label>
          <input
            type="number"
            step="0.1"
            value={customWeight}
            onChange={(e) => setCustomWeight(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm text-gray-300 mb-1">
            Value (gp) - Default: {category !== 'packs' && BASE_ITEMS[category] && BASE_ITEMS[category][baseType] ? BASE_ITEMS[category][baseType].baseValue : 'N/A'}
          </label>
          <input
            type="number"
            step="0.01"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            className="w-full p-2 bg-gray-800 text-white rounded"
          />
        </div>
          </>
        )}


        {/* [2026-05-25]: Equipment Slot + Attunement = customize fields;
            hidden in find mode. equipSlot/requiresAttunement defaults
            come from BASE_ITEMS on submit when the user hasn't overridden. */}
        {/* Equipment Slot Selection */}
        {/* [PHASE B / #008]: Options are now data-driven from
            EQUIPMENT_SLOT_LABELS so this dialog and EditItemDialog
            render identical option sets. The pre-fix hand-rolled list
            had values like 'eyes' / 'shoulders' / 'chest' / 'twoHand' /
            'mainHand' that no BASE_ITEMS row used and that
            EditItemDialog also did not list — saving against them
            produced items the equipment system could not address. */}
        {!isFindMode && category !== 'packs' && (category === 'weapons' || category === 'armor' || category === 'jewelry' || category === 'items') && (
          <div className="mb-4" data-tutorial="add-item-equip-slot">
            {/* [SUB-PLAN 1R — 2026-05-29]: Replaced inline <select> with
                ReferencePicker kind="equipSlot". The composer registry now owns
                the 26-slot vocabulary (enums.js EQUIPMENT_SLOT_LABELS) so this
                picker automatically includes 'ioun', 'misc', and any future slots
                added to enums.js without touching ItemDialog again.
                Pre-fix: hand-rolled <select> iterating EQUIPMENT_SLOT_LABELS
                inline — same data source but bypassing the composer substrate.
                The "None (Not Equippable)" option is emitted as placeholder when
                value is falsy (ReferencePicker renders placeholder text for '').
                Import of EQUIPMENT_SLOT_LABELS below kept because it's still
                used by EditItemDialog if it hasn't been migrated yet — but the
                ItemDialog render no longer reads it here. */}
            <label className="block text-xs text-gray-400 mb-1">Equipment Slot</label>
            <ReferencePicker
              kind="equipSlot"
              value={equipSlot || ''}
              onChange={setEquipSlot}
              placeholder="None (Not Equippable)"
            />
            
            {equipSlot && (
              <label className="flex items-center text-sm text-gray-400 mt-2">
                <input
                  type="checkbox"
                  checked={requiresAttunement}
                  onChange={(e) => setRequiresAttunement(e.target.checked)}
                  className="mr-2"
                />
                Requires Attunement
              </label>
            )}
          </div>
        )}

        {/* Armor Properties — required for correct AC calculation */}
        {/* [2026-05-25]: Armor properties are author-side details; hidden
            in find mode since the picker just uses BASE_ITEMS defaults. */}
        {!isFindMode && category === 'armor' && (
          <div className="mb-4 p-3 bg-gray-700 rounded border border-gray-600">
            <h4 className="text-sm font-semibold text-gray-200 mb-3">Armor Properties</h4>

            {/* Armor Type — [SUB-PLAN 2F kind (g) — 2026-05-25]: inline <select>
                → ReferencePicker kind="armorType". Rich onChange delegated to
                handleArmorTypeChange (extracted helper: sets armorType + derives
                maxDexMod/stealthDisadvantage defaults). */}
            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">Armor Type *</label>
              <ReferencePicker
                kind="armorType"
                value={armorType}
                onChange={handleArmorTypeChange}
                placeholder="-- Armor Type --"
              />
            </div>

            {/* Max DEX Bonus */}
            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">
                Max DEX Bonus {maxDexMod === null ? '(No cap)' : `(+${maxDexMod} max)`}
              </label>
              <select
                value={maxDexMod === null ? 'null' : String(maxDexMod)}
                onChange={(e) => setMaxDexMod(e.target.value === 'null' ? null : parseInt(e.target.value))}
                className="w-full p-2 bg-gray-800 text-white rounded text-sm"
              >
                <option value="null">No cap (Light / Shield)</option>
                <option value="2">+2 max (Medium)</option>
                <option value="0">No DEX bonus (Heavy)</option>
              </select>
            </div>

            {/* Strength Requirement */}
            <div className="mb-3">
              <label className="block text-xs text-gray-400 mb-1">Strength Requirement (0 = none)</label>
              <input
                type="number"
                min="0"
                max="30"
                value={strengthRequirement}
                onChange={(e) => setStrengthRequirement(parseInt(e.target.value) || 0)}
                className="w-full p-2 bg-gray-800 text-white rounded text-sm"
              />
            </div>

            {/* Stealth Disadvantage */}
            <label className="flex items-center text-sm text-gray-300">
              <input
                type="checkbox"
                checked={stealthDisadvantage}
                onChange={(e) => setStealthDisadvantage(e.target.checked)}
                className="mr-2"
              />
              Stealth Disadvantage
            </label>
          </div>
        )}

        {/* [2026-05-25]: Advanced Properties is the deepest authoring
            surface (magic bonus, statBonus, resistances, charges, spell
            assignment, treasure, etc.). Always hidden in find mode. */}
        {!isFindMode && (
        <div className="mb-4" data-tutorial="add-item-advanced">
          <button
            type="button"
            onClick={() => setAdvancedExpanded(!advancedExpanded)}
            className="w-full flex items-center justify-between p-2 bg-gradient-to-r from-amber-800 to-orange-800 rounded border border-amber-600 hover:from-amber-700 hover:to-orange-700 transition-colors"
          >
            <span className="text-sm font-bold text-amber-300">
              ⚒️ Advanced Properties
            </span>
            <ChevronRight className={`h-4 w-4 text-amber-300 transform transition-transform ${advancedExpanded ? 'rotate-90' : ''}`} />
          </button>
          
          {advancedExpanded && (
            <div className="mt-2 p-3 bg-gradient-to-r from-amber-900 to-orange-900 rounded border border-amber-600">
              <div className="space-y-4">
                
                {/* Material Enhancement */}
                <div>
                  <label className="block text-xs text-amber-200 mb-1">Material</label>
                  <select
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-amber-600"
                  >
                    <option value="">Standard Material</option>
                    <option value="adamantine">Adamantine (+durability, immunity to critical hits)</option>
                    <option value="silver">Silvered (+damage vs lycanthropes)</option>
                    <option value="cold_iron">Cold Iron (+damage vs fey)</option>
                    <option value="mithral">Mithral (-weight, +stealth for armor)</option>
                  </select>
                </div>

                {/* Magic Bonus */}
                <div>
                  <label className="block text-xs text-amber-200 mb-1">Magic Enhancement</label>
                  <select
                    value={magicBonus}
                    onChange={(e) => setMagicBonus(e.target.value)}
                    className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-amber-600"
                  >
                    <option value="">Non-magical</option>
                    <option value="1">+1 Enhancement</option>
                    <option value="2">+2 Enhancement</option>
                    <option value="3">+3 Enhancement</option>
                  </select>
                </div>

                {/* Damage Dice Override (for weapons) */}
                {(category === 'weapons' || customName.toLowerCase().includes('weapon')) && (
                  <div>
                    <label className="block text-xs text-amber-200 mb-1">Damage Dice Override</label>
                    <input
                      type="text"
                      value={damageDice}
                      onChange={(e) => setDamageDice(e.target.value)}
                      placeholder="e.g., 2d6, 1d8+2"
                      className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-amber-600"
                    />
                  </div>
                )}

                {/* Stone 4 — 2026-05-26: StatModificationsList replaces the single-slot
                    set/add/subtract picker. Canonical shape: array. Adapter handles
                    load from legacy shapes (single-slot and EditItemDialog object). */}
                <div>
                  <label className="block text-xs text-amber-200 mb-1">Stat Modifications</label>
                  <StatModificationsList
                    value={statModifications}
                    onChange={setStatModifications}
                  />
                </div>

                {/* [SUB-PLAN 1N — 2026-05-27]: HP / AC / Initiative / Saves bonus fields.
                    Writes to canonical S1 effects[] only — NOT to statModifications S4. */}
                <div>
                  <label className="block text-xs font-bold text-amber-200 mb-2">Modifiers</label>
                  <div className="flex gap-3 mb-2">
                    <div>
                      <label className="block text-xs text-amber-200 mb-1">HP Bonus</label>
                      <input
                        type="number"
                        value={bonusHP}
                        onChange={(e) => setBonusHP(e.target.value)}
                        placeholder="0"
                        style={{ width: '60px' }}
                        className="p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-amber-200 mb-1">AC Bonus</label>
                      <input
                        type="number"
                        value={acBonus}
                        onChange={(e) => setAcBonus(e.target.value)}
                        placeholder="0"
                        style={{ width: '60px' }}
                        className="p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-amber-200 mb-1">Initiative Bonus</label>
                      <input
                        type="number"
                        value={initiativeBonus}
                        onChange={(e) => setInitiativeBonus(e.target.value)}
                        placeholder="0"
                        style={{ width: '60px' }}
                        className="p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                      />
                    </div>
                  </div>
                  <div className="text-xs text-amber-300 mb-1 font-semibold">Saving Throw Bonuses</div>
                  <div className="grid grid-cols-4 gap-1">
                    {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma', 'all'].map((save) => (
                      <div key={save}>
                        <label className="block text-xs text-amber-200 mb-0.5 capitalize">{save === 'all' ? 'All' : save.slice(0, 3).charAt(0).toUpperCase() + save.slice(1, 3)}</label>
                        <input
                          type="number"
                          value={saves[save] || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSaves(prev => ({ ...prev, [save]: val }));
                          }}
                          placeholder="0"
                          style={{ width: '52px' }}
                          className="p-1 bg-gray-800 text-white rounded text-xs border border-amber-600"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* [SUB-PLAN 1H-B — 2026-05-28]: Items become the 6th member
                    of the CW authoring quintet by sharing FeatureEffectsSection.
                    Mirror of EditItemDialog (Phase B / #026 + Stones 5-6). The
                    section is purely presentational — it dispatches to the
                    callbacks bound here to mutate the local `effects` state.
                    On submit, the state merges with buildEffectsFromForm output
                    into item.effects so itemEffectsRegistration sees ALL
                    user-authored passive effects (ac/attackBonus/damageBonus/
                    proficiency grants/sense grants/advantage/resistance) when
                    the item is equipped. Items don't use the choice block — stub
                    callbacks satisfy FeatureEffectsSection's contract. */}
                <div className="mt-2">
                  <FeatureEffectsSection
                    effects={effects}
                    hasChoices={false}
                    choiceKey={''}
                    choiceOptions={[]}
                    onAddEffect={() => {
                      setEffects(prev => [...prev, emptyEffect()]);
                    }}
                    onRemoveEffect={(idx) => {
                      setEffects(prev => prev.filter((_, j) => j !== idx));
                    }}
                    onUpdateEffect={(idx, field, value) => {
                      setEffects(prev => prev.map((eff, j) =>
                        j === idx ? { ...eff, [field]: value } : eff
                      ));
                    }}
                    onToggleHasChoices={() => {}}
                    onUpdateChoiceKey={() => {}}
                    onAddChoiceOption={() => {}}
                    onRemoveChoiceOption={() => {}}
                    onUpdateChoiceOptionName={() => {}}
                    onAddChoiceOptionEffect={() => {}}
                    onRemoveChoiceOptionEffect={() => {}}
                    onUpdateChoiceOptionEffect={() => {}}
                  />
                </div>

                {/* Spell Properties + Charges wrapper */}
                <div data-tutorial="add-item-charges-spells">
                <div>
                  <h5 className="text-xs font-bold text-violet-300 mb-2 flex items-center">
                    ✨ Granted Spells
                    {itemSpells.length > 0 && (
                      <span className="ml-2 text-xs bg-violet-800 px-2 py-1 rounded">
                        {itemSpells.length} spell{itemSpells.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </h5>
                  
                  <button
                    type="button"
                    onClick={addSpell}
                    className="w-full p-2 bg-violet-700 hover:bg-violet-600 text-white rounded text-xs transition-colors mb-2"
                  >
                    + Add Spell Ability
                  </button>
                  
                  {itemSpells.map((spell, index) => (
                    <div key={spell.id} className="bg-gray-800 p-2 rounded border border-violet-500 mb-2">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-violet-300">Spell {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removeSpell(spell.id)}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          {spell.name ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-purple-300">{spell.name}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectingSpellForId(spell.id);
                                  setShowSpellSelection(true);
                                }}
                                className="text-blue-400 hover:text-blue-300 text-xs"
                              >
                                Change
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectingSpellForId(spell.id);
                                setShowSpellSelection(true);
                              }}
                              className="w-full p-1 bg-purple-700 hover:bg-purple-600 text-white rounded text-xs"
                            >
                              Select Spell
                            </button>
                          )}
                        </div>
                        <div>
                          <input
                            type="number"
                            min="1"
                            max="10"
                            value={spell.usesPerDay}
                            onChange={(e) => updateSpell(spell.id, 'usesPerDay', parseInt(e.target.value) || 1)}
                            placeholder="Uses/day"
                            className="w-full p-1 bg-gray-700 text-white rounded text-xs"
                          />
                        </div>
                      </div>
                      {/* [SUB-PLAN 1H-D-ui — 2026-05-28]: Charges per cast — only show when the item
                          has a shared charge pool (charges / maxCharges set). This lets authors author
                          items like Staff of Fire where Burning Hands = 1 charge, Fireball = 3 charges.
                          When null, the spell falls back to usesPerDay (per-spell tracking). */}
                      {(charges || maxCharges) ? (
                        <div className="mt-2">
                          <label className="block text-xs text-cyan-300 mb-1">
                            Charges per cast
                            <span className="text-gray-400 ml-1">(blank = uses own uses/day counter)</span>
                          </label>
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={spell.costPerCast ?? ''}
                            onChange={(e) => {
                              const raw = e.target.value;
                              updateSpell(spell.id, 'costPerCast', raw === '' ? null : (parseInt(raw) || null));
                            }}
                            placeholder="e.g. 3"
                            className="w-24 p-1 bg-gray-700 text-white rounded text-xs border border-cyan-700"
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                {/* Charges System */}
                <div>
                  <h5 className="text-xs font-bold text-cyan-300 mb-2">⚡ Charges</h5>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="block text-xs text-cyan-200 mb-1">Current Charges</label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={charges}
                        onChange={(e) => setCharges(e.target.value)}
                        placeholder="0"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-cyan-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-cyan-200 mb-1">Max Charges</label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={maxCharges}
                        onChange={(e) => setMaxCharges(e.target.value)}
                        placeholder="0"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-cyan-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Recharge Type (only show if charges exist) */}
                {(charges > 0 || maxCharges > 0) && (
                  <div>
                    <label className="block text-xs text-cyan-200 mb-1">Recharge Type</label>
                    <select
                      value={rechargeType}
                      onChange={(e) => setRechargeType(e.target.value)}
                      className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-cyan-600"
                    >
                      <option value={RECHARGE_TYPES.NONE}>No Recharge</option>
                      <option value={RECHARGE_TYPES.SHORT_REST}>Short Rest</option>
                      <option value={RECHARGE_TYPES.LONG_REST}>Long Rest</option>
                      <option value={RECHARGE_TYPES.DAWN}>Dawn</option>
                      <option value={RECHARGE_TYPES.DAILY}>Daily (24 hours)</option>
                      <option value={RECHARGE_TYPES.WEEKLY}>Weekly</option>
                      <option value={RECHARGE_TYPES.MONTHLY}>Monthly</option>
                      <option value={RECHARGE_TYPES.CONSUMABLE}>Consumable (No Recharge)</option>
                      <option value={RECHARGE_TYPES.SPECIAL}>Special Conditions</option>
                    </select>
                    {rechargeType === RECHARGE_TYPES.SPECIAL && (
                      <p className="text-xs text-cyan-400 mt-1">Describe special recharge conditions in the item description</p>
                    )}
                  </div>
                )}
                </div>{/* end charges-spells wrapper */}

                {/* [PHASE 10 - NOV 20, 2025]: RESTORED REMOVED FEATURES */}
                {/* [WHY]: These D&D 5e properties existed in old system, required for SRD item recreation */}

                {/* Rarity System — [SUB-PLAN 2F kind (g) — 2026-05-25]: inline <select>
                    → ReferencePicker kind="rarity". Pure setter onChange (no helper needed). */}
                <div>
                  <label className="block text-xs text-amber-200 mb-1">Item Rarity</label>
                  <ReferencePicker
                    kind="rarity"
                    value={rarity}
                    onChange={setRarity}
                    placeholder="-- Rarity --"
                  />
                </div>

                {/* Attunement Prerequisite (only show if requiresAttunement is checked) */}
                {requiresAttunement && (
                  <div>
                    <label className="block text-xs text-amber-200 mb-1">Attunement Prerequisite</label>
                    <input
                      type="text"
                      value={attunementPrerequisite}
                      onChange={(e) => setAttunementPrerequisite(e.target.value)}
                      placeholder="e.g., by a spellcaster, by a cleric, by a paladin"
                      className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-amber-600"
                    />
                    <p className="text-xs text-amber-400 mt-1">Leave blank if no specific prerequisite required</p>
                  </div>
                )}

                {/* Cursed Item */}
                <div>
                  <label className="flex items-center text-xs text-red-300 mb-1">
                    <input
                      type="checkbox"
                      checked={isCursed}
                      onChange={(e) => setIsCursed(e.target.checked)}
                      className="mr-2"
                    />
                    Cursed Item
                  </label>
                  {isCursed && (
                    <textarea
                      value={curseDescription}
                      onChange={(e) => setCurseDescription(e.target.value)}
                      placeholder="Describe the curse and its effects..."
                      rows="2"
                      className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-red-600"
                    />
                  )}
                </div>

                {/* Resistance/Immunity + Speed + Senses + Condition Immunities wrapper */}
                <div data-tutorial="add-item-defensive">
                  {/* [SUB-PLAN 1R — 2026-05-29 / Rule 30c]: Retired 2 inline <select multiple>
                      blocks that each hardcoded the 13 SRD damage types.
                      Pre-fix: any new damage type added to DAMAGE_TYPES_FEAT in
                      customFeatVocabulary.js would NOT appear here — 2 hand-rolled
                      duplicates needed individual updates.
                      Post-fix: both pickers pull from composerCatalogRegistry
                      kind="damageResistanceType" which reads DAMAGE_TYPES_FEAT.
                      Value arrays (resistances / immunities) are unchanged string[]
                      — no data migration needed. chipVariant semantics:
                      red = resistance (defensive, partial), amber = immunity (full). */}
                  <h5 className="text-xs font-bold text-green-300 mb-2">🛡️ Damage Resistance/Immunity</h5>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs text-green-200 mb-1">Grants Resistance To:</label>
                      <ReferencePicker
                        kind="damageResistanceType"
                        multi
                        value={Array.isArray(resistances) ? resistances : []}
                        onChange={setResistances}
                        chipVariant="red"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-green-200 mb-1">Grants Immunity To:</label>
                      <ReferencePicker
                        kind="damageResistanceType"
                        multi
                        value={Array.isArray(immunities) ? immunities : []}
                        onChange={setImmunities}
                        chipVariant="amber"
                      />
                    </div>
                  </div>
                </div>

                {/* Weapon Properties (only for weapons) */}
                {(category === 'weapons' || customName.toLowerCase().includes('weapon')) && (
                  <div>
                    <h5 className="text-xs font-bold text-red-300 mb-2">⚔️ Weapon Properties</h5>
                    {/* [PHASE B / #030 — 2026-05-06]: Iterate the shared
                        WEAPON_PROPERTIES data block (`src/data/weaponProperties.js`)
                        instead of the prior inline literal array. Each
                        checkbox carries a `title` (mouse-hover tooltip
                        with the PHB description) and `aria-describedby`
                        pointing at a hidden description span (screen
                        reader access). */}
                    <div className="grid grid-cols-3 gap-2">
                      {WEAPON_PROPERTIES.map(prop => (
                        <label
                          key={prop.id}
                          className="flex items-center text-xs text-white cursor-help"
                          title={prop.description}
                        >
                          <input
                            type="checkbox"
                            checked={weaponProperties.includes(prop.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setWeaponProperties([...weaponProperties, prop.id]);
                              } else {
                                setWeaponProperties(weaponProperties.filter(p => p !== prop.id));
                              }
                            }}
                            aria-describedby={`wp-desc-${prop.id}`}
                            className="mr-1"
                          />
                          {prop.label}
                          <span
                            id={`wp-desc-${prop.id}`}
                            className="sr-only"
                          >
                            {prop.description}
                          </span>
                        </label>
                      ))}
                    </div>

                    {/* Range fields (for ranged weapons) */}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <label className="block text-xs text-red-200 mb-1">Normal Range (ft)</label>
                        <input
                          type="number"
                          value={normalRange}
                          onChange={(e) => setNormalRange(e.target.value)}
                          placeholder="e.g., 80"
                          className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-red-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-red-200 mb-1">Long Range (ft)</label>
                        <input
                          type="number"
                          value={longRange}
                          onChange={(e) => setLongRange(e.target.value)}
                          placeholder="e.g., 320"
                          className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-red-600"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Speed Modifiers */}
                <div>
                  <h5 className="text-xs font-bold text-blue-300 mb-2">🏃 Speed Bonuses</h5>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-blue-200 mb-1">Speed Bonus (ft)</label>
                      <input
                        type="number"
                        value={speedBonus}
                        onChange={(e) => setSpeedBonus(e.target.value)}
                        placeholder="e.g., 10"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-blue-200 mb-1">Flying Speed (ft)</label>
                      <input
                        type="number"
                        value={flyingSpeed}
                        onChange={(e) => setFlyingSpeed(e.target.value)}
                        placeholder="e.g., 60"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-blue-200 mb-1">Swimming Speed (ft)</label>
                      <input
                        type="number"
                        value={swimmingSpeed}
                        onChange={(e) => setSwimmingSpeed(e.target.value)}
                        placeholder="e.g., 40"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-blue-600"
                      />
                    </div>
                    {/* [SUB-PLAN 1H-H Stage 1 — 2026-05-29]: Speed multiply + minimum fields */}
                    <div>
                      <label className="block text-xs text-blue-200 mb-1">Speed Multiplier (×)</label>
                      <input
                        type="number"
                        value={speedMultiply}
                        onChange={(e) => setSpeedMultiply(e.target.value)}
                        placeholder="e.g., 2"
                        min="1"
                        step="0.5"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-blue-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-blue-200 mb-1">Speed Minimum (ft)</label>
                      <input
                        type="number"
                        value={speedMinimum}
                        onChange={(e) => setSpeedMinimum(e.target.value)}
                        placeholder="e.g., 30"
                        min="5"
                        step="5"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-blue-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Senses */}
                <div>
                  <h5 className="text-xs font-bold text-indigo-300 mb-2">👁️ Senses</h5>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-indigo-200 mb-1">Darkvision (ft)</label>
                      <input
                        type="number"
                        value={darkvision}
                        onChange={(e) => setDarkvision(e.target.value)}
                        placeholder="e.g., 60"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-indigo-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-indigo-200 mb-1">Blindsight (ft)</label>
                      <input
                        type="number"
                        value={blindsight}
                        onChange={(e) => setBlindsight(e.target.value)}
                        placeholder="e.g., 30"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-indigo-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-indigo-200 mb-1">Tremorsense (ft)</label>
                      <input
                        type="number"
                        value={tremorsense}
                        onChange={(e) => setTremorsense(e.target.value)}
                        placeholder="e.g., 60"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-indigo-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-indigo-200 mb-1">Truesight (ft)</label>
                      <input
                        type="number"
                        value={truesight}
                        onChange={(e) => setTruesight(e.target.value)}
                        placeholder="e.g., 60"
                        className="w-full p-2 bg-gray-800 text-white rounded text-xs border border-indigo-600"
                      />
                    </div>
                  </div>
                </div>

                {/* Advantage Situations — [SUB-PLAN 1H-C-ui — 2026-05-28]:
                    Closes GAP-8. itemEffectsRegistration.js L278-282 already emits
                    { type:'advantage', target, operation:'advantage' } for each entry
                    in form.advantageOn. The emitter was present but ItemDialog had
                    no UI to populate it. ReferencePicker(kind="advantageTarget") pulls
                    the ADVANTAGE_TARGETS catalog registered in composerCatalogRegistry.
                    Value flows through advantageOn state → buildEffectsFromForm →
                    effectRegistry → effectCalculator.getAdvantageTargets(). */}
                <div>
                  <label className="block text-xs text-blue-200 mb-1">Grants Advantage On:</label>
                  <ReferencePicker
                    kind="advantageTarget"
                    multi
                    value={Array.isArray(advantageOn) ? advantageOn : []}
                    onChange={setAdvantageOn}
                    chipVariant="blue"
                    placeholder="Add a roll this item grants advantage on..."
                  />
                </div>

                {/* Condition Immunities — [SUB-PLAN 2F kind (b) — 2026-05-25]:
                    ReferencePicker(kind="condition") replaces the 14-entry
                    inline <select multiple>. Canonical SRD source is
                    src/data/srdConditions.js (14 binary conditions);
                    custom additions via playerStats.customConditions
                    (Sub-plan 3B Condition Creator queued). Drops
                    `exhaustion` (multi-level, tracked separately) and
                    gains `invisible` (was missing from inline list). */}
                <div>
                  <label className="block text-xs text-yellow-200 mb-1">Grants Immunity to Conditions:</label>
                  <ReferencePicker
                    kind="condition"
                    multi
                    value={conditionImmunities}
                    onChange={setConditionImmunities}
                    chipVariant="yellow"
                    placeholder="Add a condition this item grants immunity to..."
                  />
                </div>

                <div className="text-xs text-amber-300 bg-amber-800 p-2 rounded">
                  💡 <strong>Tip:</strong> Advanced properties let you create custom items like "Adamantine Dagger +1" or weapons with spell abilities
                </div>
              </div>
            </div>
          )}
        </div>
        )}
        {/* End Advanced Properties (find-mode hidden) */}

        {/* [SUB-PLAN 4L — 2026-05-28]: Ammunition Effects (on-hit) section.
            Visible only when the selected BASE_ITEMS row has an ammoType (i.e., the
            user is authoring ammunition). Lets players author triggeredEffect on-hit
            payloads that match the SRD ammo shape (gameData.js L502-581).
            Hidden in find mode (authoring-only). */}
        {!isFindMode && category === 'ammunition' && (
          <div className="mb-4 p-3 bg-gradient-to-r from-emerald-900 to-teal-900 rounded border border-emerald-600">
            <h4 className="text-sm font-bold text-emerald-300 mb-3">
              ✨ Ammunition Effects (on-hit)
            </h4>

            {/* Complex-effects notice when multiple triggeredEffect rows exist */}
            {(BASE_ITEMS?.ammunition?.[baseType]?.itemEffects?.filter(e => e.type === 'triggeredEffect')?.length > 1) && (
              <div className="mb-3 p-2 bg-yellow-800 bg-opacity-50 rounded text-xs text-yellow-200">
                This item has multiple triggered effects and cannot be edited here. View the item description for details.
              </div>
            )}

            {/* Enable/disable toggle */}
            <label className="flex items-center text-sm text-emerald-200 mb-3">
              <input
                type="checkbox"
                checked={ammoEffectsEnabled}
                onChange={(e) => setAmmoEffectsEnabled(e.target.checked)}
                className="mr-2"
              />
              Apply effects on hit
            </label>

            {ammoEffectsEnabled && (
              <div className="space-y-3">

                {/* Attack bonus */}
                <div>
                  <label className="block text-xs text-emerald-200 mb-1">Attack Bonus (+N, 0 = none)</label>
                  <input
                    type="number"
                    value={ammoAttackBonus}
                    onChange={(e) => setAmmoAttackBonus(e.target.value)}
                    placeholder="0"
                    style={{ width: '80px' }}
                    className="p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                  />
                </div>

                {/* Damage bonus — flat vs dice toggle */}
                <div>
                  <label className="block text-xs text-emerald-200 mb-1">Damage Bonus</label>
                  <div className="flex gap-3 mb-2">
                    <label className="flex items-center text-xs text-emerald-200">
                      <input
                        type="radio"
                        name="ammo-dmg-type"
                        checked={!ammoUseDice}
                        onChange={() => setAmmoUseDice(false)}
                        className="mr-1"
                      />
                      Flat (+N)
                    </label>
                    <label className="flex items-center text-xs text-emerald-200">
                      <input
                        type="radio"
                        name="ammo-dmg-type"
                        checked={ammoUseDice}
                        onChange={() => setAmmoUseDice(true)}
                        className="mr-1"
                      />
                      Dice
                    </label>
                  </div>

                  {!ammoUseDice ? (
                    <input
                      type="number"
                      value={ammoDamageBonusFlat}
                      onChange={(e) => setAmmoDamageBonusFlat(e.target.value)}
                      placeholder="e.g., 1"
                      style={{ width: '80px' }}
                      className="p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-emerald-200 mb-1">Dice (e.g. 1d4)</label>
                        <input
                          type="text"
                          value={ammoDamageDice}
                          onChange={(e) => setAmmoDamageDice(e.target.value)}
                          placeholder="1d4"
                          className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-emerald-200 mb-1">Damage Type</label>
                        <select
                          value={ammoDamageType}
                          onChange={(e) => setAmmoDamageType(e.target.value)}
                          className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                        >
                          <option value="">— Type —</option>
                          <option value="acid">Acid</option>
                          <option value="bludgeoning">Bludgeoning</option>
                          <option value="cold">Cold</option>
                          <option value="fire">Fire</option>
                          <option value="force">Force</option>
                          <option value="lightning">Lightning</option>
                          <option value="necrotic">Necrotic</option>
                          <option value="piercing">Piercing</option>
                          <option value="poison">Poison</option>
                          <option value="psychic">Psychic</option>
                          <option value="radiant">Radiant</option>
                          <option value="slashing">Slashing</option>
                          <option value="thunder">Thunder</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* Save mechanic (only relevant for dice-damage) */}
                {ammoUseDice && (
                  <div className="border-t border-emerald-700 pt-3">
                    <label className="flex items-center text-xs text-emerald-200 mb-2">
                      <input
                        type="checkbox"
                        checked={ammoSaveEnabled}
                        onChange={(e) => setAmmoSaveEnabled(e.target.checked)}
                        className="mr-1"
                      />
                      Save mechanic (target saves to reduce/avoid damage)
                    </label>
                    {ammoSaveEnabled && (
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-emerald-200 mb-1">Save DC</label>
                          <input
                            type="number"
                            value={ammoSaveDC}
                            onChange={(e) => setAmmoSaveDC(e.target.value)}
                            placeholder="10"
                            className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-emerald-200 mb-1">Ability</label>
                          <select
                            value={ammoSaveAbility}
                            onChange={(e) => setAmmoSaveAbility(e.target.value)}
                            className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                          >
                            <option value="strength">STR</option>
                            <option value="dexterity">DEX</option>
                            <option value="constitution">CON</option>
                            <option value="intelligence">INT</option>
                            <option value="wisdom">WIS</option>
                            <option value="charisma">CHA</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-emerald-200 mb-1">Half on Save</label>
                          <label className="flex items-center text-xs text-emerald-200 mt-2">
                            <input
                              type="checkbox"
                              checked={ammoHalfOnSave}
                              onChange={(e) => setAmmoHalfOnSave(e.target.checked)}
                              className="mr-1"
                            />
                            Yes
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* [1H-J — 2026-05-29]: Target creature type filter (optional) */}
                <div>
                  <label className="block text-xs text-emerald-200 mb-1">Target creature type (optional)</label>
                  <input
                    type="text"
                    value={ammoTargetType}
                    onChange={(e) => setAmmoTargetType(e.target.value)}
                    placeholder="e.g. dragon, undead — leave blank for all"
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-emerald-600"
                  />
                </div>

                {/* Read-only: source label + trigger condition */}
                <div className="border-t border-emerald-700 pt-2 text-xs text-emerald-400 space-y-1">
                  <div>
                    <span className="font-semibold">Source label:</span>{' '}
                    {customName || (baseType ? baseType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—')}
                  </div>
                  <div>
                    <span className="font-semibold">Trigger:</span>{' '}
                    On hit (ammoTypeMatch: {BASE_ITEMS?.ammunition?.[baseType]?.ammoType || '—'})
                    {ammoTargetType.trim() && (
                      <span> · targetTypeMatch: {ammoTargetType.trim().toLowerCase()}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* [SUB-PLAN 1H-G Stage 1 — 2026-05-29]: Consumable Use Effects section.
            Visible only for consumable items (category === 'consumables').
            Visibility gate: category === 'consumables'. This matches the BASE_ITEMS
            bucket key used for all SRD potions (gameData.js consumables bucket).
            Custom items authored in this category gain healing/tempHP/condition/damage
            use-effects that potionEffectApply.buildAppliedPotionEffect reads at drink-time.
            Shape emitted by buildEffectsFromForm matches trace (b) exactly.
            Hidden in find-mode (authoring surface only). */}
        {!isFindMode && category === 'consumables' && (
          <div className="mb-4 p-3 bg-gradient-to-r from-purple-900 to-indigo-900 rounded border border-purple-600">
            <h4 className="text-sm font-bold text-purple-300 mb-3">
              🧪 Use Effects (on drink/use)
            </h4>
            <p className="text-xs text-purple-300 mb-3">
              Define what happens when this item is consumed. Effects are applied immediately (healing/damage) or as timed buffs (condition/temp HP).
            </p>

            {/* Healing Roll */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Healing Roll (dice, e.g. 2d4+2)</label>
              <input
                type="text"
                value={healingRoll}
                onChange={(e) => setHealingRoll(e.target.value)}
                placeholder="e.g. 2d4+2, 4d4+4"
                className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
              />
            </div>

            {/* Temp HP */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Temp HP (flat number, e.g. 10)</label>
              <input
                type="number"
                value={useEffectTempHP}
                onChange={(e) => setUseEffectTempHP(e.target.value)}
                placeholder="e.g. 10"
                style={{ width: '100px' }}
                className="p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
              />
            </div>

            {/* Damage on use */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Damage on Use (dice + type, e.g. poison)</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Dice (e.g. 3d6)</label>
                  <input
                    type="text"
                    value={useEffectDamageValue}
                    onChange={(e) => setUseEffectDamageValue(e.target.value)}
                    placeholder="3d6"
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  />
                </div>
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Damage Type</label>
                  <select
                    value={useEffectDamageType}
                    onChange={(e) => setUseEffectDamageType(e.target.value)}
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  >
                    <option value="">— Type —</option>
                    <option value="acid">Acid</option>
                    <option value="bludgeoning">Bludgeoning</option>
                    <option value="cold">Cold</option>
                    <option value="fire">Fire</option>
                    <option value="force">Force</option>
                    <option value="lightning">Lightning</option>
                    <option value="necrotic">Necrotic</option>
                    <option value="piercing">Piercing</option>
                    <option value="poison">Poison</option>
                    <option value="psychic">Psychic</option>
                    <option value="radiant">Radiant</option>
                    <option value="slashing">Slashing</option>
                    <option value="thunder">Thunder</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Condition grant rows */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Grant Condition on Use (e.g. invisible, poisoned)</label>
              {grantConditions.map((row, idx) => (
                <div key={idx} className="mb-2 p-2 bg-gray-800 rounded border border-purple-700">
                  <div className="grid grid-cols-2 gap-2 mb-1">
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Condition Key</label>
                      <input
                        type="text"
                        value={row.conditionKey || ''}
                        onChange={(e) => {
                          const updated = [...grantConditions];
                          updated[idx] = { ...updated[idx], conditionKey: e.target.value };
                          setGrantConditions(updated);
                        }}
                        placeholder="e.g. invisible, poisoned"
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Save Ability (opt.)</label>
                      <select
                        value={row.saveAbility || ''}
                        onChange={(e) => {
                          const updated = [...grantConditions];
                          updated[idx] = { ...updated[idx], saveAbility: e.target.value };
                          setGrantConditions(updated);
                        }}
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      >
                        <option value="">— None —</option>
                        <option value="strength">STR</option>
                        <option value="dexterity">DEX</option>
                        <option value="constitution">CON</option>
                        <option value="intelligence">INT</option>
                        <option value="wisdom">WIS</option>
                        <option value="charisma">CHA</option>
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Save DC (opt.)</label>
                      <input
                        type="number"
                        value={row.saveDC || ''}
                        onChange={(e) => {
                          const updated = [...grantConditions];
                          updated[idx] = { ...updated[idx], saveDC: e.target.value };
                          setGrantConditions(updated);
                        }}
                        placeholder="e.g. 13"
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Recurring Dmg (opt.)</label>
                      <input
                        type="text"
                        value={row.recurringDamage || ''}
                        onChange={(e) => {
                          const updated = [...grantConditions];
                          updated[idx] = { ...updated[idx], recurringDamage: e.target.value };
                          setGrantConditions(updated);
                        }}
                        placeholder="e.g. 3d6"
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Dmg Type (opt.)</label>
                      <input
                        type="text"
                        value={row.damageType || ''}
                        onChange={(e) => {
                          const updated = [...grantConditions];
                          updated[idx] = { ...updated[idx], damageType: e.target.value };
                          setGrantConditions(updated);
                        }}
                        placeholder="e.g. poison"
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setGrantConditions(grantConditions.filter((_, i) => i !== idx))}
                    className="mt-1 text-xs text-red-400 hover:text-red-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setGrantConditions([...grantConditions, { conditionKey: '', saveDC: '', saveAbility: '', recurringDamage: '', damageType: '' }])}
                className="text-xs text-purple-300 hover:text-purple-100 border border-purple-600 px-2 py-1 rounded"
              >
                + Add Condition
              </button>
            </div>

            {/* [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Feature grants (climbSpeed / flySpeed / waterBreathing) */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Grant Feature on Use (e.g. climbSpeed, flySpeed, waterBreathing)</label>
              {featureGrants.map((row, idx) => (
                <div key={idx} className="mb-2 p-2 bg-gray-800 rounded border border-purple-700">
                  <div className="grid grid-cols-2 gap-2 mb-1">
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Feature Target</label>
                      <select
                        value={row.target || ''}
                        onChange={(e) => {
                          const updated = [...featureGrants];
                          // Auto-set operation when target is picked
                          const t = e.target.value;
                          const op = t === 'waterBreathing' ? 'grant'
                            : t === 'flySpeed' ? 'equal_to_walk_speed'
                            : 'set';
                          updated[idx] = { ...updated[idx], target: t, operation: op,
                            value: (t === 'climbSpeed' || t === 'flySpeed') && op === 'set' ? 'walk' : undefined };
                          setFeatureGrants(updated);
                        }}
                        className="w-full p-1 bg-gray-700 text-white rounded text-xs border border-purple-600"
                      >
                        <option value="">— Feature —</option>
                        <option value="climbSpeed">Climb Speed (equal to walk)</option>
                        <option value="flySpeed">Fly Speed (equal to walk)</option>
                        <option value="waterBreathing">Water Breathing</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-purple-300 mb-1">Operation (auto)</label>
                      <input
                        type="text"
                        readOnly
                        value={row.operation || ''}
                        className="w-full p-1 bg-gray-600 text-gray-300 rounded text-xs border border-purple-700"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFeatureGrants(featureGrants.filter((_, i) => i !== idx))}
                    className="mt-1 text-xs text-red-400 hover:text-red-200"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setFeatureGrants([...featureGrants, { target: '', operation: 'grant' }])}
                className="text-xs text-purple-300 hover:text-purple-100 border border-purple-600 px-2 py-1 rounded"
              >
                + Add Feature Grant
              </button>
            </div>

            {/* [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Breath Weapon (e.g. fire breath potion) */}
            <div className="mb-3">
              <label className="block text-xs text-purple-200 mb-1">Breath Weapon (e.g. 4d6 fire, DC 13 DEX)</label>
              <div className="grid grid-cols-3 gap-2 mb-1">
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Damage Dice</label>
                  <input
                    type="text"
                    value={breathWeaponValue}
                    onChange={(e) => setBreathWeaponValue(e.target.value)}
                    placeholder="e.g. 4d6"
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  />
                </div>
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Damage Type</label>
                  <select
                    value={breathWeaponDamageType}
                    onChange={(e) => setBreathWeaponDamageType(e.target.value)}
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  >
                    <option value="fire">Fire</option>
                    <option value="cold">Cold</option>
                    <option value="lightning">Lightning</option>
                    <option value="acid">Acid</option>
                    <option value="poison">Poison</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Uses</label>
                  <input
                    type="number"
                    value={breathWeaponUses}
                    onChange={(e) => setBreathWeaponUses(e.target.value)}
                    placeholder="e.g. 3"
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Save DC (opt.)</label>
                  <input
                    type="number"
                    value={breathWeaponSaveDC}
                    onChange={(e) => setBreathWeaponSaveDC(e.target.value)}
                    placeholder="e.g. 13"
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  />
                </div>
                <div>
                  <label className="block text-xs text-purple-300 mb-1">Save Ability</label>
                  <select
                    value={breathWeaponSaveAbility}
                    onChange={(e) => setBreathWeaponSaveAbility(e.target.value)}
                    className="w-full p-1 bg-gray-800 text-white rounded text-xs border border-purple-600"
                  >
                    <option value="dex">DEX</option>
                    <option value="con">CON</option>
                    <option value="str">STR</option>
                    <option value="int">INT</option>
                    <option value="wis">WIS</option>
                    <option value="cha">CHA</option>
                  </select>
                </div>
              </div>
            </div>

            {/* [SUB-PLAN 1H-G Stage 2 — 2026-05-29]: Damage Reduction All (potion_invulnerability) */}
            <div className="mb-3">
              <label className="flex items-center text-xs text-purple-200">
                <input
                  type="checkbox"
                  checked={damageReductionAll}
                  onChange={(e) => setDamageReductionAll(e.target.checked)}
                  className="mr-2"
                />
                Resistance to ALL damage types on use (potion of invulnerability)
              </label>
            </div>
          </div>
        )}

        {/* [2026-05-25]: Purchase-for-free stays visible in BOTH modes.
            User direction: the checkbox must be available in find mode
            so a player can bypass auto-purchase when grabbing an item
            from the catalog. Without it, addNewItem can silently fail
            on insufficient funds when settings.autoPurchaseItems=true. */}
        <div className="mb-4" data-tutorial="add-item-free">
          <label className="flex items-center text-sm text-gray-300">
            <input
              type="checkbox"
              checked={purchaseForFree}
              onChange={(e) => setPurchaseForFree(e.target.checked)}
              className="mr-2"
            />
            Purchase for free (bypass cost)
          </label>
        </div>

        {/* Save to Source Option */}
        {/* [2026-05-25]: Save-to-Source is pure authoring (writes to
            sourcesManager). Hidden in find mode — picker is read-only. */}
        {!isFindMode && (
        <div className="mb-4 p-3 bg-gray-700 rounded" data-tutorial="add-item-save-source">
          <label className="flex items-center text-sm text-gray-300 mb-2">
            <input
              type="checkbox"
              checked={saveToSource}
              onChange={(e) => {
                setSaveToSource(e.target.checked);
                if (e.target.checked && !selectedSource) {
                  // Auto-select first custom source
                  const sources = sourcesManager.getAllSources();
                  const customSources = Object.keys(sources).filter(id => sources[id].removable);
                  if (customSources.length > 0) {
                    setSelectedSource(customSources[0]);
                  }
                }
              }}
              className="mr-2"
            />
            Save to Source (make reusable)
          </label>
          {saveToSource && (
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="w-full p-2 bg-gray-800 text-white rounded text-sm"
            >
              <option value="">Select a source...</option>
              {/* [FIX NOV 28, 2025]: Include custom source - check editable OR id === 'custom' */}
              {Object.entries(sourcesManager.getAllSources())
                .filter(([id, source]) => source.editable || id === 'custom')
                .map(([id, source]) => (
                  <option key={id} value={id}>
                    {source.name}
                  </option>
                ))}
            </select>
          )}
        </div>
        )}
        {/* End Save to Source (find-mode hidden) */}

        <div className="p-4 bg-gray-800 border-t border-gray-700 flex justify-end gap-2">
          <button
            onClick={handleCloseDialog}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white rounded"
          >
            Add Item
          </button>
        </div>
      </div>
    </MobileModal>

    {/* Spell Selection Dialog */}
    {showSpellSelection && (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
        <div className="bg-gray-800 rounded-lg p-4 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
          <h3 className="text-xl font-bold mb-4">Select Spell for Magic Item</h3>

          {/* [FIX 2025-10-21]: getSpellsByClass returns ARRAY, not object - properly handle it */}
          {!getSpellsByClass ? (
            <div className="text-center text-gray-400 py-4">
              Loading spell data...
            </div>
          ) : (
          <div className="space-y-2">
            {/* [v1.0]: Get all spellcasting classes dynamically — supports custom classes */}
            {Object.keys(getAllClasses()).filter(cn => isSpellcaster(cn)).map(className => {
              const spells = getSpellsByClass(className);
              // [FIX]: Check if spells data is valid before processing
              if (!spells || !Array.isArray(spells) || spells.length === 0) return null;

              // Group spells by level client-side
              const spellsByLevel = {};
              spells.forEach(spell => {
                const level = spell.level || 0;
                if (!spellsByLevel[level]) {
                  spellsByLevel[level] = [];
                }
                spellsByLevel[level].push(spell);
              });

              // Render grouped spells
              // [FIX NOV 28, 2025]: Changed from .slice(0, 5) to .slice(0, 10) for all spell levels 0-9
              return Object.entries(spellsByLevel)
                .sort(([a], [b]) => parseInt(a) - parseInt(b))
                .slice(0, 10) // Show all spell levels (0-9)
                .map(([level, spellList]) => (
                <div key={`${className}-${level}`}>
                  <h4 className="text-sm font-bold text-gray-400 mt-2">
                    {className.charAt(0).toUpperCase() + className.slice(1)} - {level === '0' ? 'Cantrips' : `Level ${level}`}
                  </h4>
                  <div className="grid grid-cols-2 gap-1">
                    {spellList.slice(0, 10).map(spell => (
                      <button
                        key={`${className}-${spell.name}`}
                        onClick={() => {
                          updateSpell(selectingSpellForId, 'name', spell.name);
                          updateSpell(selectingSpellForId, 'level', parseInt(level));
                          updateSpell(selectingSpellForId, 'damage', spell.damage || null);
                          updateSpell(selectingSpellForId, 'saveType', spell.save || null);
                          setShowSpellSelection(false);
                          setSelectingSpellForId(null);
                        }}
                        className="text-left p-1 bg-gray-700 hover:bg-gray-600 rounded text-xs truncate"
                      >
                        {spell.name}
                      </button>
                    ))}
                  </div>
                </div>
              ));
            }).flat()}
          </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                setShowSpellSelection(false);
                setSelectingSpellForId(null);
              }}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ItemDialog;